import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation } from 'react-router-dom';
import MarkUsedModal from '../components/MarkUsedModal';
import RestockPickerModal from '../components/RestockPickerModal';
import './V9OrdersPage.css';

async function parseJsonSafe(res) {
  try { return await res.json(); } catch (_) { return null; }
}

function readJsonCache(key) {
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (_) { return null; }
}

function writeJsonCache(key, value) {
  try { window.sessionStorage.setItem(key, JSON.stringify(value)); } catch (_) { /* ignore */ }
}

function formatQty(value) {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return '0';
  if (Math.abs(num % 1) < 1e-9) return String(Math.trunc(num));
  return num.toFixed(2).replace(/\.?0+$/, '');
}

function formatQtyOrBlank(value) {
  const num = Number(value || 0);
  if (!Number.isFinite(num) || Math.abs(num) < 1e-9) return '';
  return formatQty(num);
}

function formatWarningAge(hours) {
  if (hours < 24) {
    return `${formatQty(hours)}h`;
  }
  const days = Math.floor(hours / 24);
  const remainingHours = Math.round(hours % 24);
  return `${days}d ${remainingHours}h`;
}

function compactIdList(ids = [], limit = 2) {
  const list = Array.isArray(ids)
    ? ids.map((id) => String(id || '').trim()).filter(Boolean)
    : [];
  if (list.length <= limit) return list.join(', ');
  const head = list.slice(0, limit).join(', ');
  return `${head} +${list.length - limit} more`;
}

function formatDateTimeOrDash(value) {
  const raw = String(value || '').trim();
  if (!raw) return '-';
  const dt = new Date(raw);
  if (!Number.isFinite(dt.getTime())) return raw;
  return dt.toLocaleString();
}

function formatCurrency(value) {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return '0.00';
  return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatStatusLabel(status) {
  return String(status || '-').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function orderStatusTone(status) {
  const s = String(status || '').trim().toUpperCase();
  if (s === 'DELIVERED' || s === 'CLOSED') return 'green';
  if (s === 'IN_TRANSIT' || s === 'PACKED' || s === 'READY_TO_PACK') return 'blue';
  if (s === 'PRODUCTION' || s === 'ALLOCATING') return 'amber';
  if (s === 'ON_HOLD') return 'red';
  if (s === 'IMPORTED') return 'slate';
  if (s.includes('RTO') || s.includes('RETURN') || s === 'CANCELLED') return 'red';
  return 'slate';
}

function invoiceCoverageLabel(state) {
  const key = String(state || '').trim().toUpperCase();
  if (key === 'FULL') return 'Full';
  if (key === 'PARTIAL') return 'Partial';
  if (key === 'MISMATCH') return 'Mismatch';
  return 'Not Invoiced';
}

function invoiceCoverageTone(state) {
  const key = String(state || '').trim().toUpperCase();
  if (key === 'FULL') return 'good';
  if (key === 'PARTIAL') return 'warn';
  if (key === 'MISMATCH') return 'bad';
  return 'muted';
}

const V9_LIST_CACHE_PREFIX = 'v9_orders_list_cache:';
const V9_DETAIL_CACHE_PREFIX = 'v9_order_detail_cache:';


function deriveInvoiceCoverageFromLines(lines = []) {
  const parsed = Array.isArray(lines) ? lines : [];
  const EPS = 1e-6;
  let totalRequired = 0;
  let totalInvoiced = 0;
  let mismatchCount = 0;
  parsed.forEach((line) => {
    const required = Math.max(0, Number(line?.netRequiredQty || 0));
    const invoiced = Math.max(0, Number(line?.invoicedQty || 0));
    totalRequired += required;
    totalInvoiced += invoiced;
    if (Math.abs(invoiced - required) > EPS) mismatchCount += 1;
  });
  const deltaQty = Number((totalInvoiced - totalRequired).toFixed(3));
  const hasInvoiced = totalInvoiced > EPS;
  const hasExcess = parsed.some((line) => Number(line?.invoicedQty || 0) > Number(line?.netRequiredQty || 0) + EPS);
  let state = 'NOT_INVOICED';
  if (hasInvoiced) {
    if (mismatchCount === 0) state = 'FULL';
    else if (!hasExcess && totalInvoiced < totalRequired - EPS) state = 'PARTIAL';
    else state = 'MISMATCH';
  }
  return {
    state,
    totalRequiredQty: Number(totalRequired.toFixed(3)),
    totalInvoicedQty: Number(totalInvoiced.toFixed(3)),
    deltaQty,
    mismatchCount,
  };
}

function isPreShipOrderStatus(status) {
  const s = String(status || '').trim().toUpperCase();
  return ['IMPORTED', 'ALLOCATING', 'PRODUCTION', 'READY_TO_PACK', 'PACKED', 'AWAITING_SHIPMENT_WEBHOOK'].includes(s);
}

function getExcessTargetQty(line = {}) {
  const shippedQty = Math.max(0, Number(line?.shippedQty || 0));
  if (shippedQty > 0.000001) return shippedQty;
  return Math.max(0, Number(line?.netRequiredQty || 0));
}

function hasOwn(obj, key) {
  return Object.prototype.hasOwnProperty.call(obj || {}, key);
}

function getBundleComponentQtyPerUnit(line = {}) {
  if (!line || line.isBundle !== true) return 0;
  const explicit = Number(line.componentQtyPerBundle || 0);
  if (Number.isFinite(explicit) && explicit > 0) return explicit;
  const components = Array.isArray(line.bundleComponents) ? line.bundleComponents : [];
  return components.reduce((sum, component) => {
    const qty = Number(component?.quantity || component?.qty || component?.componentQty || 0);
    return sum + (Number.isFinite(qty) && qty > 0 ? qty : 0);
  }, 0);
}

function isBundleLineForQty(line = {}) {
  return line?.isBundle === true && getBundleComponentQtyPerUnit(line) > 0;
}

function getComparableReceivedQty(line = {}) {
  if (isBundleLineForQty(line)) {
    if (hasOwn(line, 'bundleReceivedQty')) return Math.max(0, Number(line.bundleReceivedQty || 0));
    const perUnit = getBundleComponentQtyPerUnit(line);
    const raw = Math.max(0, Number(line.receivedQty || 0));
    const required = Math.max(0, Number(line.netRequiredQty || 0));
    if (perUnit > 0 && raw > required + 1e-6) {
      return Math.floor((raw / perUnit) + 1e-9);
    }
  }
  return Math.max(0, Number(line?.receivedQty || 0));
}

function getComparableInBatchQty(line = {}) {
  if (isBundleLineForQty(line)) {
    if (hasOwn(line, 'bundleInBatchQty')) return Math.max(0, Number(line.bundleInBatchQty || 0));
    const perUnit = getBundleComponentQtyPerUnit(line);
    const raw = Math.max(0, Number(line.inBatchQty || 0));
    const required = Math.max(0, Number(line.netRequiredQty || 0));
    if (perUnit > 0 && raw > required + 1e-6) {
      return Math.floor((raw / perUnit) + 1e-9);
    }
  }
  return Math.max(0, Number(line?.inBatchQty || 0));
}

function getExcessPendingQty(line = {}) {
  const receivedQty = getComparableReceivedQty(line);
  const targetQty = getExcessTargetQty(line);
  const baseExcess = Math.max(0, receivedQty - targetQty);
  const restockedQty = Math.max(0, Number(line?.restockedQty || 0));
  const excessRestockedQty = Math.max(0, Number(line?.excessRestockedQty || 0));
  return Math.max(0, Number((baseExcess - restockedQty - excessRestockedQty).toFixed(3)));
}

function shouldCheckStockEligibilityForLine(order = {}, line = {}) {
  const orderStatus = String(order?.orderStatus || '').trim().toUpperCase();
  if (!['IMPORTED', 'ALLOCATING', 'PRODUCTION'].includes(orderStatus)) return false;

  const netRequiredQty = Math.max(0, Number(line?.netRequiredQty || 0));
  const receivedQty = getComparableReceivedQty(line);
  const inBatchQty = getComparableInBatchQty(line);
  const packedQty = Math.max(0, Number(line?.packedQty || 0));
  const shippedQty = Math.max(0, Number(line?.shippedQty || 0));
  const lineStatus = String(line?.lineStatus || '').trim().toUpperCase();
  const pendingQty = Math.max(0, netRequiredQty - receivedQty - inBatchQty);

  if (netRequiredQty <= 1e-6) return false;
  if (pendingQty <= 1e-6) return false; // already fully received/used
  if (inBatchQty > 1e-6) return false; // already sent to batch
  if (packedQty > 1e-6 || shippedQty > 1e-6) return false; // post-pack/ship no stock check
  if (['IN_BATCH', 'PACKED', 'SHIPPED', 'DELIVERED', 'CANCELLED', 'RTO_RETURNED', 'CUSTOMER_RETURNED'].includes(lineStatus)) return false;

  return true;
}

const WARNING_SUB_PILLS = [
  { value: 'ALL', label: 'All Warnings' },
  { value: 'ALLOCATING', label: 'Allocating' },
  { value: 'PRODUCTION', label: 'Production' },
  { value: 'READY_TO_PACK', label: 'Ready to Pack' },
  { value: 'PACKED', label: 'Packed' },
  { value: 'IN_TRANSIT', label: 'In Transit' },
  { value: 'RTO_IN_PROGRESS', label: 'RTO In Progress' },
];

function V9OrdersPage() {
  const { currentUser, canPerformAction } = useAuth();
  const location = useLocation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [linkedSoSyncBusy, setLinkedSoSyncBusy] = useState(false);
  const [resolveSoPendingBusy, setResolveSoPendingBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState('');

  const [draftStatus, setDraftStatus] = useState('ALL');
  const [draftWarningType, setDraftWarningType] = useState('ALL');
  const [draftProcessingMode, setDraftProcessingMode] = useState('ALL');
  const [draftSearch, setDraftSearch] = useState('');
  const [draftSearchField, setDraftSearchField] = useState('orderNumber');
  const [draftIncludeClosed, setDraftIncludeClosed] = useState(false);
  const [applied, setApplied] = useState({
    status: 'ALL',
    processingMode: 'ALL',
    search: '',
    searchField: 'orderNumber',
    includeClosed: false,
    warningType: 'ALL',
  });
  const [selectedOrderId, setSelectedOrderId] = useState('');
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailMsg, setDetailMsg] = useState('');
  const [detailData, setDetailData] = useState(null);
  const [detailEligibilityLoading, setDetailEligibilityLoading] = useState(false);
  const [detailWarnings, setDetailWarnings] = useState([]);
  const [detailWarningsLoading, setDetailWarningsLoading] = useState(false);
  const [packModalOpen, setPackModalOpen] = useState(false);
  const [packForceMode, setPackForceMode] = useState(false);
  const [packPartner, setPackPartner] = useState('delhivery');
  const [packAwb, setPackAwb] = useState('');
  const [packSubmitting, setPackSubmitting] = useState(false);
  const [lineEligibility, setLineEligibility] = useState({});
  const [piecePickerOpen, setPiecePickerOpen] = useState(false);
  const [piecePickerLine, setPiecePickerLine] = useState(null);
  const [piecePickerSubmitting, setPiecePickerSubmitting] = useState(false);
  const [restockPickerOpen, setRestockPickerOpen] = useState(false);
  const [restockPickerCtx, setRestockPickerCtx] = useState({ sku: '', orderNumber: '', productImage: '', productName: '' });
  const [processPoModalOpen, setProcessPoModalOpen] = useState(false);
  const [processPoSubmitting, setProcessPoSubmitting] = useState(false);
  const [processPoSelectedSkus, setProcessPoSelectedSkus] = useState([]);
  const [invoiceBusy, setInvoiceBusy] = useState(false);
  const [pendingOpenPoModalOrderId, setPendingOpenPoModalOrderId] = useState('');
  const [actionMenuOrderId, setActionMenuOrderId] = useState('');
  // ── Bulk select / bulk invoice refresh ──
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [selectedOrderIds, setSelectedOrderIds] = useState(new Set());
  const [bulkRefreshRunning, setBulkRefreshRunning] = useState(false);
  const [bulkRefreshResults, setBulkRefreshResults] = useState(null);
  const [debugOpen, setDebugOpen] = useState(false);
  const [showHiddenLines, setShowHiddenLines] = useState(false);
  const [auditLogItems, setAuditLogItems] = useState([]);
  const [auditLogExpanded, setAuditLogExpanded] = useState(false);
  const [auditLogLoading, setAuditLogLoading] = useState(false);
  const [auditLogError, setAuditLogError] = useState(null);
  const [sortBy, setSortBy] = useState('updatedAt');
  const [sortDir, setSortDir] = useState('desc');
  const [statusCounts, setStatusCounts] = useState({});
  const [statusTotalCount, setStatusTotalCount] = useState(0);
  const [unmappedShipmentNotice, setUnmappedShipmentNotice] = useState({
    totalUnmapped: 0,
    items: [],
    statusPairs: [],
    computedAt: '',
    loading: false,
    loaded: false,
    error: '',
  });
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const detailRequestSeqRef = useRef(0);
  const autoRefreshIntervalRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const statusPills = useMemo(() => ([
    { value: 'ALL', label: 'All' },
    { value: 'IMPORTED', label: 'Imported' },
    { value: 'ON_HOLD', label: 'On Hold' },
    { value: 'SALES_ORDER_PENDING', label: 'SO Pending' },
    { value: 'INVOICE_PENDING', label: 'Invoice Pending' },
    { value: 'INVOICE_REVIEW', label: 'Invoice Review' },
    { value: 'WARNINGS', label: 'Warnings' },
    { value: 'ALLOCATING', label: 'Allocating' },
    { value: 'PRODUCTION', label: 'Production' },
    { value: 'READY_TO_PACK', label: 'Ready To Pack' },
    { value: 'PACKED', label: 'Packed' },
    { value: 'IN_TRANSIT', label: 'In Transit' },
    { value: 'DELIVERED', label: 'Delivered' },
    { value: 'RTO_IN_PROGRESS', label: 'RTO In Progress' },
    { value: 'RTO_RECEIVED', label: 'RTO Received' },
    { value: 'CUSTOMER_RETURN_IN_PROGRESS', label: 'Customer Return In Progress' },
    { value: 'RETURN_RECEIVED', label: 'Return Received' },
    { value: 'CANCELLED', label: 'Cancelled' },
    { value: 'CLOSED', label: 'Closed' },
  ]), []);

  useEffect(() => {
    if (!actionMenuOrderId) return undefined;
    const onDocMouseDown = (event) => {
      const target = event?.target;
      if (!target || typeof target.closest !== 'function') {
        setActionMenuOrderId('');
        return;
      }
      const insideMenu = !!(target.closest('.v9o-kebab-menu') || target.closest('.v9orders-kebab-menu'));
      const insideButton = !!(target.closest('.v9o-kebab-btn') || target.closest('.v9orders-kebab-btn'));
      if (!insideMenu && !insideButton) {
        setActionMenuOrderId('');
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
    };
  }, [actionMenuOrderId]);

  useEffect(() => {
    setShowHiddenLines(false);
  }, [selectedOrderId]);

  const authedFetch = useCallback(async (url, options = {}) => {
    const token = await currentUser.getIdToken();
    const hasBody = options && Object.prototype.hasOwnProperty.call(options, 'body');
    const contentType = hasBody ? { 'Content-Type': 'application/json' } : {};
    return fetch(url, {
      ...options,
      headers: {
        ...contentType,
        Authorization: `Bearer ${token}`,
        ...((options && options.headers) || {}),
      },
    });
  }, [currentUser]);

  const buildUrl = useCallback((cursor = '') => {
    const params = new URLSearchParams();
    params.set('limit', '50');
    if (applied.status !== 'ALL') params.set('status', applied.status);
    if (applied.status === 'WARNINGS' && applied.warningType !== 'ALL') {
      params.set('warningType', applied.warningType);
    }
    if (applied.processingMode !== 'ALL') params.set('processingMode', applied.processingMode);
    if (applied.search) {
      params.set('search', applied.search);
      params.set('searchField', applied.searchField || 'orderNumber');
    }
    if (applied.includeClosed) params.set('includeClosed', 'true');
    if (cursor) params.set('cursor', cursor);
    return `/api/v9/orders?${params.toString()}`;
  }, [applied]);

  const listCacheKey = useMemo(
    () => `${V9_LIST_CACHE_PREFIX}${encodeURIComponent(JSON.stringify(applied || {}))}`,
    [applied]
  );

  const fetchLineEligibility = useCallback(async (orderId, lines = [], order = null) => {
    if (!Array.isArray(lines) || lines.length === 0) return {};
    const checksNeeded = lines.filter((line) => shouldCheckStockEligibilityForLine(order || {}, line || {}));
    const checks = await Promise.all(
      checksNeeded.map(async (line) => {
        const resCheck = await authedFetch(`/api/v9/orders/${encodeURIComponent(orderId)}/lines/${encodeURIComponent(line.sku)}/stock-candidates`);
        const dataCheck = await parseJsonSafe(resCheck);
        return { sku: line.sku, ok: !!resCheck.ok && !!dataCheck?.ok, result: dataCheck?.result || null };
      })
    );
    const map = {};
    lines.forEach((line) => {
      const pendingQty = Math.max(
        0,
        Number(line?.netRequiredQty || 0) - getComparableReceivedQty(line) - Math.max(0, getComparableInBatchQty(line))
      );
      map[line.sku] = {
        pendingQty: Number(pendingQty.toFixed(2)),
        totalAvailable: 0,
        inStockEligible: false,
        canMarkUsed: false,
        pieces: [],
      };
    });
    checks.forEach((c) => {
      map[c.sku] = c.ok && c.result
        ? c.result
        : { pendingQty: 0, totalAvailable: 0, inStockEligible: false, canMarkUsed: false, pieces: [] };
    });
    return map;
  }, [authedFetch]);

  const resetDetailState = useCallback(() => {
    setDetailData(null);
    setLineEligibility({});
    setDetailWarnings([]);
    setDetailWarningsLoading(false);
    setDetailMsg('');
    setPackModalOpen(false);
    setPackForceMode(false);
    setPackSubmitting(false);
    setPackPartner('delhivery');
    setPackAwb('');
    setPiecePickerOpen(false);
    setPiecePickerLine(null);
    setPiecePickerSubmitting(false);
    setRestockPickerOpen(false);
    setRestockPickerCtx({ sku: '', orderNumber: '', productImage: '', productName: '' });
    setProcessPoModalOpen(false);
    setProcessPoSubmitting(false);
    setProcessPoSelectedSkus([]);
    setInvoiceBusy(false);
    setPendingOpenPoModalOrderId('');
    setAuditLogItems([]);
    setAuditLogExpanded(false);
    setAuditLogError(null);
  }, []);

  const loadFirstPage = useCallback(async ({ preferCache = false, silent = false } = {}) => {
    let hasCached = false;
    if (preferCache) {
      const cached = readJsonCache(listCacheKey);
      const cachedItems = Array.isArray(cached?.items) ? cached.items : [];
      if (cachedItems.length > 0 || cached?.hasMore === true) {
        hasCached = true;
        setRows(cachedItems);
        setHasMore(cached?.hasMore === true);
        setNextCursor(String(cached?.nextCursor || ''));
        if (!silent) setMsg('Showing cached orders. Updating...');
      }
    }
    if (!silent) {
      setLoading(!hasCached);
      if (!hasCached) setMsg('Loading v9 orders...');
    }
    try {
      const res = await authedFetch(buildUrl());
      const data = await parseJsonSafe(res);
      if (!res.ok || !data?.ok) {
        if (!silent) setMsg(data?.error?.message || 'Failed to load v9 orders.');
        if (!hasCached) {
          setRows([]);
          setHasMore(false);
          setNextCursor('');
        }
        return;
      }
      const items = Array.isArray(data?.result?.items) ? data.result.items : [];
      setRows(items);
      setHasMore(data?.result?.hasMore === true);
      const freshNextCursor = String(data?.result?.nextCursor || '');
      setNextCursor(freshNextCursor);
      setLastRefreshedAt(Date.now());
      if (!silent) setMsg(items.length === 0 ? 'No v9 orders found for current filters.' : `Loaded ${items.length} orders.`);
      writeJsonCache(listCacheKey, {
        items,
        hasMore: data?.result?.hasMore === true,
        nextCursor: freshNextCursor,
        updatedAt: Date.now(),
      });
    } finally {
      if (!silent) setLoading(false);
    }
  }, [authedFetch, buildUrl, listCacheKey]);

  const loadStatusCounts = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (applied.processingMode !== 'ALL') params.set('processingMode', applied.processingMode);
      if (applied.search) params.set('search', applied.search);
      if (applied.includeClosed) params.set('includeClosed', 'true');
      const res = await authedFetch(`/api/v9/orders/status-counts?${params.toString()}`);
      const data = await parseJsonSafe(res);
      if (!res.ok || !data?.ok) return;
      const counts = data?.result?.countsByStatus && typeof data.result.countsByStatus === 'object'
        ? data.result.countsByStatus
        : {};
      setStatusCounts(counts);
      setStatusTotalCount(Number(data?.result?.totalCount || 0));
    } catch (_) {
      // keep previous counts
    }
  }, [applied.includeClosed, applied.processingMode, applied.search, authedFetch]);

  const loadUnmappedShipmentNotice = useCallback(async ({ force = false } = {}) => {
    if (!currentUser) return;
    setUnmappedShipmentNotice((prev) => ({ ...prev, loading: true, error: '' }));
    try {
      const params = new URLSearchParams();
      params.set('limit', '6');
      if (force) params.set('force', 'true');
      const res = await authedFetch(`/api/v9/ops/unmapped-shipment-statuses?${params.toString()}`);
      const data = await parseJsonSafe(res);
      if (!res.ok || !data?.ok) {
        throw new Error(data?.error?.message || 'Failed to load unmapped shipment statuses.');
      }
      setUnmappedShipmentNotice({
        totalUnmapped: Number(data?.result?.totalUnmapped || 0),
        items: Array.isArray(data?.result?.items) ? data.result.items : [],
        statusPairs: Array.isArray(data?.result?.statusPairs) ? data.result.statusPairs : [],
        computedAt: String(data?.result?.computedAt || '').trim(),
        loading: false,
        loaded: true,
        error: '',
      });
    } catch (err) {
      setUnmappedShipmentNotice((prev) => ({
        ...prev,
        loading: false,
        loaded: true,
        error: err?.message || 'Failed to load unmapped shipment statuses.',
      }));
    }
  }, [authedFetch, currentUser]);

  const copyUnmappedShipmentDetails = useCallback(async () => {
    const items = Array.isArray(unmappedShipmentNotice.items) ? unmappedShipmentNotice.items : [];
    if (!items.length) return;
    const text = items.map((row) => (
      `Order ${row.orderNumber || row.matchedOrderId} | ${String(row.provider || '').toUpperCase()} | ${row.externalStatus || '-'} | AWB ${row.awb || '-'} | Event ${row.eventTime || row.createdAt || '-'}`
    )).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setMsg('Unmapped shipment details copied.');
    } catch (_) {
      setMsg('Failed to copy unmapped shipment details.');
    }
  }, [unmappedShipmentNotice.items]);

  const openV9MappingPage = useCallback(() => {
    window.open('/admin/v9-ops', '_blank', 'noopener,noreferrer');
  }, []);

  const loadMore = useCallback(async () => {
    if (!hasMore || !nextCursor) return;
    setLoadingMore(true);
    try {
      const res = await authedFetch(buildUrl(nextCursor));
      const data = await parseJsonSafe(res);
      if (!res.ok || !data?.ok) {
        setMsg(data?.error?.message || 'Failed to load more orders.');
        return;
      }
      const items = Array.isArray(data?.result?.items) ? data.result.items : [];
      const merged = [...rows, ...items];
      const mergedHasMore = data?.result?.hasMore === true;
      const mergedNextCursor = String(data?.result?.nextCursor || '');
      setRows(merged);
      setHasMore(mergedHasMore);
      setNextCursor(mergedNextCursor);
      writeJsonCache(listCacheKey, {
        items: merged,
        hasMore: mergedHasMore,
        nextCursor: mergedNextCursor,
        updatedAt: Date.now(),
      });
    } finally {
      setLoadingMore(false);
    }
  }, [authedFetch, buildUrl, hasMore, listCacheKey, nextCursor, rows]);

  useEffect(() => {
    loadFirstPage({ preferCache: true });
  }, [loadFirstPage]);

  useEffect(() => {
    loadStatusCounts();
  }, [loadStatusCounts]);

  useEffect(() => {
    loadUnmappedShipmentNotice();
  }, [loadUnmappedShipmentNotice]);

  // ── Auto-refresh every 60 s (pauses when detail open or tab hidden) ──
  useEffect(() => {
    const tick = () => {
      if (document.visibilityState !== 'visible') return;
      if (selectedOrderId) return; // user is reading a detail — don't disrupt
      loadFirstPage({ preferCache: false, silent: true });
      loadStatusCounts();
    };
    autoRefreshIntervalRef.current = setInterval(tick, 60_000);
    return () => clearInterval(autoRefreshIntervalRef.current);
  }, [loadFirstPage, loadStatusCounts, selectedOrderId]);

  // Refresh immediately when user switches back to the tab (if idle >60 s)
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      if (selectedOrderId) return;
      const staleSec = lastRefreshedAt ? (Date.now() - lastRefreshedAt) / 1000 : Infinity;
      if (staleSec >= 60) {
        loadFirstPage({ preferCache: false, silent: true });
        loadStatusCounts();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [loadFirstPage, loadStatusCounts, lastRefreshedAt, selectedOrderId]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const prefillOrderId = String(params.get('searchOrderId') || '').trim();
    if (!prefillOrderId) return;
    setDraftSearch(prefillOrderId);
    setApplied((prev) => ({ ...prev, search: prefillOrderId }));
  }, [location.search]);

  const lastUpdatedLabel = useMemo(() => {
    if (!lastRefreshedAt) return '';
    const d = new Date(lastRefreshedAt);
    return `Updated ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  }, [lastRefreshedAt]);

  const totalAmountAllRows = useMemo(() => rows.reduce((sum, row) => sum + Number(row?.amounts?.netCollectible || 0), 0), [rows]);

  const loadAuditLog = useCallback(async (orderId) => {
    if (!orderId) return;
    setAuditLogLoading(true);
    setAuditLogError(null);
    try {
      const res = await authedFetch(`/api/v9/orders/${encodeURIComponent(orderId)}/audit-log?limit=50`);
      const data = await parseJsonSafe(res);
      if (res.ok && data?.ok) {
        setAuditLogItems(Array.isArray(data?.result?.items) ? data.result.items : []);
      } else {
        setAuditLogItems([]);
        setAuditLogError(data?.error?.message || 'Failed to load activity log.');
      }
    } catch (e) {
      setAuditLogItems([]);
      setAuditLogError(e?.message || 'Failed to load activity log.');
    } finally {
      setAuditLogLoading(false);
    }
  }, [authedFetch]);

  const loadOrderWarnings = useCallback(async (orderId, requestSeq = null) => {
    const normalizedOrderId = String(orderId || '').trim();
    if (!normalizedOrderId) return;
    setDetailWarningsLoading(true);
    try {
      const res = await authedFetch(`/api/v9/orders/${encodeURIComponent(normalizedOrderId)}/stale-warnings`);
      const data = await parseJsonSafe(res);
      if (requestSeq != null && requestSeq !== detailRequestSeqRef.current) return;
      if (res.ok && data?.ok) {
        setDetailWarnings(Array.isArray(data?.result?.rows) ? data.result.rows : []);
      } else {
        setDetailWarnings([]);
      }
    } catch (_) {
      if (requestSeq == null || requestSeq === detailRequestSeqRef.current) {
        setDetailWarnings([]);
      }
    } finally {
      if (requestSeq == null || requestSeq === detailRequestSeqRef.current) {
        setDetailWarningsLoading(false);
      }
    }
  }, [authedFetch]);

  const loadOrderDetail = useCallback(async (orderId, { silent = false } = {}) => {
    const normalizedOrderId = String(orderId || '').trim();
    if (!normalizedOrderId) return;
    const requestSeq = ++detailRequestSeqRef.current;
    if (!silent) {
      setSelectedOrderId(normalizedOrderId);
      resetDetailState();
      setDetailLoading(true);
    }
    const detailCacheKey = `${V9_DETAIL_CACHE_PREFIX}${normalizedOrderId}`;
    const cached = readJsonCache(detailCacheKey);
    let hasCached = false;
    if (cached?.detailData?.order) {
      hasCached = true;
      if (requestSeq !== detailRequestSeqRef.current) return;
      setDetailData(cached.detailData);
      setPackPartner(String(cached?.detailData?.order?.shipment?.partner || 'delhivery').toLowerCase() || 'delhivery');
      setPackAwb(String(cached?.detailData?.order?.shipment?.awb || '').trim());
      setLineEligibility(cached?.lineEligibility || {});
      if (!silent) setDetailMsg('');
    }
    if (!silent) setDetailLoading(!hasCached);
    try {
      const res = await authedFetch(`/api/v9/orders/${encodeURIComponent(normalizedOrderId)}`);
      const data = await parseJsonSafe(res);
      if (requestSeq !== detailRequestSeqRef.current) return;
      if (!res.ok || !data?.ok) {
        if (!silent) setDetailMsg(data?.error?.message || 'Failed to load order details.');
        if (!hasCached) setDetailData(null);
        return;
      }
      setDetailData(data.result || null);
      setPackPartner(String(data?.result?.order?.shipment?.partner || 'delhivery').toLowerCase() || 'delhivery');
      setPackAwb(String(data?.result?.order?.shipment?.awb || '').trim());
      setPackModalOpen(false);
      setPackSubmitting(false);
      loadOrderWarnings(normalizedOrderId, requestSeq);
      const lines = Array.isArray(data?.result?.lines) ? data.result.lines : [];
      if (!silent) {
        setDetailLoading(false);
        setDetailEligibilityLoading(true);
      }
      const map = await fetchLineEligibility(normalizedOrderId, lines, data?.result?.order || null);
      if (requestSeq !== detailRequestSeqRef.current) return;
      setLineEligibility(map);
      if (!silent) {
        setDetailMsg('');
        loadAuditLog(normalizedOrderId);
      }
      writeJsonCache(detailCacheKey, {
        detailData: data.result || null,
        lineEligibility: map,
        updatedAt: Date.now(),
      });
    } finally {
      if (!silent && requestSeq === detailRequestSeqRef.current) {
        setDetailLoading(false);
        setDetailEligibilityLoading(false);
      }
    }
  }, [authedFetch, fetchLineEligibility, loadAuditLog, loadOrderWarnings, resetDetailState]);

  useEffect(() => {
    if (!currentUser) return undefined;
    const timer = window.setInterval(async () => {
      try {
        await loadFirstPage({ preferCache: false, silent: true });
        if (selectedOrderId) {
          await loadOrderDetail(selectedOrderId, { silent: true });
        }
      } catch (_) {
        // silent background refresh
      }
    }, 60_000);
    return () => window.clearInterval(timer);
  }, [currentUser, loadFirstPage, loadOrderDetail, selectedOrderId]);

  const callAction = useCallback(async (path, body = null, successMsg = 'Action completed.') => {
    try {
      setDetailMsg('Processing...');
      const res = await authedFetch(path, {
        method: 'POST',
        body: body ? JSON.stringify(body) : JSON.stringify({}),
      });
      const data = await parseJsonSafe(res);
      if (!res.ok || !data?.ok) {
        setDetailMsg(data?.error?.message || 'Action failed.');
        return false;
      }
      setDetailMsg(successMsg);
      if (selectedOrderId) await loadOrderDetail(selectedOrderId);
      await loadFirstPage({ preferCache: false });
      return true;
    } catch (error) {
      setDetailMsg(error?.message || 'Action failed.');
      return false;
    }
  }, [authedFetch, loadFirstPage, loadOrderDetail, selectedOrderId]);

  const callDeleteAction = useCallback(async (path, successMsg = 'Deleted.') => {
    try {
      setDetailMsg('Processing...');
      const res = await authedFetch(path, { method: 'DELETE' });
      const data = await parseJsonSafe(res);
      if (!res.ok || !data?.ok) {
        setDetailMsg(data?.error?.message || 'Delete failed.');
        return false;
      }
      setDetailMsg(successMsg);
      if (selectedOrderId) await loadOrderDetail(selectedOrderId);
      await loadFirstPage({ preferCache: false });
      return true;
    } catch (error) {
      setDetailMsg(error?.message || 'Delete failed.');
      return false;
    }
  }, [authedFetch, loadFirstPage, loadOrderDetail, selectedOrderId]);

  const callOrderRowAction = useCallback(async (orderId, path, body = null, successMsg = 'Action completed.') => {
    const targetOrderId = String(orderId || '').trim();
    if (!targetOrderId) return false;
    const writingOpenDetail = targetOrderId && selectedOrderId === targetOrderId;
    setMsg('Processing...');
    if (writingOpenDetail) setDetailMsg('Processing...');
    try {
      const res = await authedFetch(path, {
        method: 'POST',
        body: body ? JSON.stringify(body) : JSON.stringify({}),
      });
      const data = await parseJsonSafe(res);
      if (!res.ok || !data?.ok) {
        const err = data?.error?.message || 'Action failed.';
        setMsg(err);
        if (writingOpenDetail) setDetailMsg(err);
        return false;
      }
      setMsg(successMsg);
      if (writingOpenDetail) {
        setDetailMsg(successMsg);
        await loadOrderDetail(targetOrderId);
      }
      await loadFirstPage({ preferCache: false, silent: true });
      return true;
    } catch (error) {
      const err = error?.message || 'Action failed.';
      setMsg(err);
      if (writingOpenDetail) setDetailMsg(err);
      return false;
    }
  }, [authedFetch, loadFirstPage, loadOrderDetail, selectedOrderId]);

  const syncLinkedZohoSalesOrders = useCallback(async () => {
    const ok = window.confirm('Sync linked Zoho Sales Orders for current v9 orders now?');
    if (!ok) return;
    try {
      setLinkedSoSyncBusy(true);
      setMsg('Syncing linked Zoho sales orders...');
      const res = await authedFetch('/api/v9/sales-orders/sync-linked', {
        method: 'POST',
        body: JSON.stringify({ limit: 400 }),
      });
      const data = await parseJsonSafe(res);
      if (!res.ok || !data?.ok) {
        setMsg(data?.error?.message || 'Linked SO sync failed.');
        return;
      }
      const stats = data?.result || {};
      setMsg(
        `Linked SO sync done: scanned ${Number(stats?.scannedOrders || 0)}, ` +
        `eligible ${Number(stats?.eligibleOrders || 0)}, ` +
        `synced ${Number(stats?.syncedOrders || 0)}, ` +
        `errors ${Number(stats?.errors || 0)}.`
      );
      await loadFirstPage({ preferCache: false, silent: true });
      await loadStatusCounts();
      if (selectedOrderId) await loadOrderDetail(selectedOrderId, { silent: true });
    } catch (error) {
      setMsg(error?.message || 'Linked SO sync failed.');
    } finally {
      setLinkedSoSyncBusy(false);
    }
  }, [authedFetch, loadFirstPage, loadOrderDetail, loadStatusCounts, selectedOrderId]);

  const resolvePendingZohoSalesOrders = useCallback(async () => {
    const ok = window.confirm('Resolve SO Pending by linking existing Zoho SO (reference-based, no create) now?');
    if (!ok) return;
    try {
      setResolveSoPendingBusy(true);
      setMsg('Resolving SO Pending links...');
      const res = await authedFetch('/api/v9/sales-orders/resolve-pending-links', {
        method: 'POST',
        body: JSON.stringify({ limit: 1000 }),
      });
      const data = await parseJsonSafe(res);
      if (!res.ok || !data?.ok) {
        setMsg(data?.error?.message || 'Resolve SO Pending failed.');
        return;
      }
      const stats = data?.result || {};
      setMsg(
        `SO Pending resolve done: scanned ${Number(stats?.scannedOrders || 0)}, ` +
        `eligible ${Number(stats?.eligibleOrders || 0)}, ` +
        `linked ${Number(stats?.linkedOrders || 0)}, ` +
        `still pending ${Number(stats?.skippedCreateDisabled || 0)}, ` +
        `errors ${Number(stats?.errors || 0)}.`
      );
      await loadFirstPage({ preferCache: false, silent: true });
      await loadStatusCounts();
      if (selectedOrderId) await loadOrderDetail(selectedOrderId, { silent: true });
    } catch (error) {
      setMsg(error?.message || 'Resolve SO Pending failed.');
    } finally {
      setResolveSoPendingBusy(false);
    }
  }, [authedFetch, loadFirstPage, loadOrderDetail, loadStatusCounts, selectedOrderId]);

  const copyDebugJson = useCallback(async (payload, label = 'Debug JSON') => {
    try {
      const text = JSON.stringify(payload, null, 2);
      await navigator.clipboard.writeText(text);
      setDetailMsg(`${label} copied.`);
    } catch (_) {
      setDetailMsg(`Failed to copy ${label}.`);
    }
  }, []);

  const selectedOrder = useMemo(() => {
    const order = detailData?.order || null;
    if (!order) return null;
    return String(order?.id || '').trim() === String(selectedOrderId || '').trim() ? order : null;
  }, [detailData, selectedOrderId]);
  const selectedLines = useMemo(() => {
    if (!selectedOrder) return [];
    return Array.isArray(detailData?.lines) ? detailData.lines : [];
  }, [detailData, selectedOrder]);
  const invoiceCoverage = useMemo(() => {
    const fromOrder = selectedOrder?.invoice?.coverage && typeof selectedOrder.invoice.coverage === 'object'
      ? selectedOrder.invoice.coverage
      : null;
    return fromOrder || deriveInvoiceCoverageFromLines(selectedLines);
  }, [selectedLines, selectedOrder]);
  const invoiceDoc = useMemo(() => {
    const inv = selectedOrder?.invoice;
    return inv && typeof inv === 'object' ? inv : {};
  }, [selectedOrder]);
  const invoiceReview = useMemo(() => {
    const review = invoiceDoc?.review;
    return review && typeof review === 'object' ? review : {};
  }, [invoiceDoc]);
  const invoiceCoverageState = useMemo(
    () => String(invoiceCoverage?.state || 'NOT_INVOICED').trim().toUpperCase(),
    [invoiceCoverage]
  );
  const invoiceMismatchOpen = useMemo(
    () => invoiceCoverageState === 'PARTIAL' || invoiceCoverageState === 'MISMATCH',
    [invoiceCoverageState]
  );
  const invoiceResolveEligible = useMemo(
    () => Math.abs(Number(invoiceCoverage?.deltaQty || 0)) < 1e-6 && Number(invoiceCoverage?.mismatchCount || 0) === 0,
    [invoiceCoverage]
  );
  const invoiceLineRows = useMemo(() => (
    selectedLines.map((line) => {
      const req = Number(line?.netRequiredQty || 0);
      const inv = Number(line?.invoicedQty || 0);
      return {
        sku: String(line?.sku || '').trim(),
        req,
        inv,
        delta: Number((inv - req).toFixed(3)),
      };
    }).filter((row) => row.sku)
  ), [selectedLines]);
  const invoiceDisplayRows = useMemo(
    () => invoiceLineRows.filter((row) => row.req > 0 || row.inv > 0 || Math.abs(row.delta) > 1e-6),
    [invoiceLineRows]
  );
  const invoiceReviewStatus = useMemo(
    () => String(invoiceReview?.status || '').trim().toUpperCase(),
    [invoiceReview]
  );
  const detailMsgTone = useMemo(() => {
    const text = String(detailMsg || '').trim().toLowerCase();
    if (!text) return 'info';
    if (
      text.includes('failed')
      || text.includes('error')
      || text.includes('cannot')
      || text.includes('not found')
      || text.includes('require')
      || text.includes('blocked')
    ) return 'error';
    if (
      text.includes('done')
      || text.includes('completed')
      || text.includes('packed')
      || text.includes('submitted')
      || text.includes('copied')
      || text.includes('undone')
      || text.includes('refreshed')
      || text.includes('acknowledged')
      || text.includes('resolved')
    ) return 'success';
    return 'info';
  }, [detailMsg]);
  const canMarkPackEligible = useMemo(() => {
    if (!selectedOrder || selectedLines.length === 0) return false;
    if (String(selectedOrder.orderStatus || '').toUpperCase() !== 'READY_TO_PACK') return false;
    return selectedLines.every((line) => {
      const req = Number(line.netRequiredQty || 0);
      const rec = getComparableReceivedQty(line);
      return req > 0 && Math.abs(rec - req) < 0.0001;
    });
  }, [selectedOrder, selectedLines]);

  // Admin force-pack: ALLOCATING or PRODUCTION orders that have at least one line
  const canForcePackAllocating = useMemo(() => {
    if (!selectedOrder || selectedLines.length === 0) return false;
    const status = String(selectedOrder.orderStatus || '').toUpperCase();
    if (!['ALLOCATING', 'PRODUCTION'].includes(status)) return false;
    return selectedLines.some((line) => Number(line.netRequiredQty || 0) > 0);
  }, [selectedOrder, selectedLines]);

  const canMarkRtoReceived = useMemo(() => {
    if (!selectedOrder) return false;
    const orderStatus = String(selectedOrder.orderStatus || '').trim().toUpperCase();
    if (['RTO_RECEIVED', 'RETURN_RECEIVED', 'CLOSED', 'DELIVERED'].includes(orderStatus)) return false;
    if (orderStatus === 'RTO_IN_PROGRESS') return true;
    const shipmentTokens = [
      selectedOrder?.shipment?.status,
      selectedOrder?.shipment?.normalizedStatus,
      selectedOrder?.shipment?.trackingStatus,
      selectedOrder?.shipment?.externalStatus,
    ]
      .map((v) => String(v || '').trim().toUpperCase())
      .filter(Boolean);
    return shipmentTokens.some((s) => s.includes('RTO'));
  }, [selectedOrder]);

  const canUndoUsedAtOrderLevel = useMemo(
    () => isPreShipOrderStatus(selectedOrder?.orderStatus),
    [selectedOrder]
  );

  const isRestockStatusEligible = useMemo(() => {
    const s = String(selectedOrder?.orderStatus || '').trim().toUpperCase();
    return s === 'CANCELLED' || s === 'RTO_RECEIVED' || s === 'RETURN_RECEIVED';
  }, [selectedOrder]);

  const processPoEligibleLines = useMemo(() => {
    if (!selectedOrder || selectedLines.length === 0) return [];
    const orderStatus = String(selectedOrder?.orderStatus || '').trim().toUpperCase();
    if (orderStatus !== 'IMPORTED' && orderStatus !== 'ALLOCATING' && orderStatus !== 'PRODUCTION') return [];
    return selectedLines
      .map((line) => {
        const sku = String(line?.sku || '').trim().toUpperCase();
        const elig = lineEligibility[sku] || lineEligibility[line?.sku] || {};
        const inBatchQty = getComparableInBatchQty(line);
        const pending = Math.max(0, Number(line?.netRequiredQty || 0) - getComparableReceivedQty(line) - Math.max(0, inBatchQty));
        if (pending <= 0.0001) return null;
        if (inBatchQty > 0.0001) return null;
        return {
          sku,
          qty: Number(pending.toFixed(2)),
          inBatchQty: Number(inBatchQty.toFixed(2)),
          inStockEligible: !!elig?.canMarkUsed,
          availableQty: Number(elig?.totalAvailable || 0),
          productImageUrl: String(
            line?.productImageUrl ||
            line?.imageUrl ||
            line?.image ||
            ''
          ).trim(),
        };
      })
      .filter((x) => !!x?.sku);
  }, [lineEligibility, selectedLines, selectedOrder]);

  const canProcessByPoEligible = useMemo(() => processPoEligibleLines.length > 0, [processPoEligibleLines]);

  const openPiecePicker = useCallback((line) => {
    const sku = String(line?.sku || '').trim().toUpperCase();
    const elig = lineEligibility[sku] || {};
    const pieces = Array.isArray(elig.pieces) ? elig.pieces : [];
    const allocationMode = String(elig?.allocationMode || line?.allocationMode || 'DIRECT').trim().toUpperCase() || 'DIRECT';
    const pendingQty = Number(elig.pendingQty || 0);
    const orderNumber = String(
      selectedOrder?.sourceLinks?.shopify?.orderName
      || selectedOrder?.sourceLinks?.zoho?.referenceNumber
      || selectedOrder?.externalOrderId
      || selectedOrderId
      || ''
    ).trim() || String(selectedOrderId || '');
    setPiecePickerLine({
      sku,
      pendingQty,
      pieces,
      orderId: selectedOrderId,
      orderNumber,
      productName: line?.productName || line?.name || line?.title || '-',
      productImage: lineEligibility[sku]?.productImageUrl || line?.image || line?.imageUrl || line?.productImage || line?.productImageUrl || '',
      orderedQty: Number(line?.netRequiredQty || 0),
      receivedQty: getComparableReceivedQty(line),
      allocationMode,
      componentSummary: Array.isArray(elig?.componentSummary) ? elig.componentSummary : [],
      componentCandidates: Array.isArray(elig?.componentCandidates) ? elig.componentCandidates : [],
    });
    setPiecePickerOpen(true);
  }, [lineEligibility, selectedOrder, selectedOrderId]);

  const confirmPiecePicker = useCallback(async (consume) => {
    if (!piecePickerLine?.sku || !selectedOrderId) return;
    setPiecePickerSubmitting(true);
    const ok = await callAction(`/api/v9/orders/${encodeURIComponent(selectedOrderId)}/mark-used`, {
      items: [{ sku: piecePickerLine.sku, consume }],
    }, 'Marked used.');
    if (ok) {
      setPiecePickerOpen(false);
      setPiecePickerLine(null);
    }
    setPiecePickerSubmitting(false);
  }, [callAction, piecePickerLine, selectedOrderId]);

  const submitPack = useCallback(async (force = false) => {
    if (!selectedOrderId) return;
    if (!force && !canMarkPackEligible) return;
    if (force && !canForcePackAllocating) return;
    if (!String(packAwb || '').trim()) {
      setDetailMsg('AWB is required to mark pack.');
      return;
    }
    setPackSubmitting(true);
    const packages = [{
      packageRef: `PKG-${Date.now()}`,
      lineItems: selectedLines.map((line) => ({ sku: line.sku, qty: Number(line.netRequiredQty || 0) })),
    }];
    const body = {
      shipmentPartner: String(packPartner || 'delhivery').toLowerCase(),
      awb: String(packAwb || '').trim(),
      packages,
      ...(force ? { forcePack: true } : {}),
    };
    const ok = await callAction(`/api/v9/orders/${encodeURIComponent(selectedOrderId)}/pack`, body, force ? 'Order force-packed.' : 'Order packed.');
    if (ok) setPackModalOpen(false);
    setPackSubmitting(false);
  }, [callAction, canForcePackAllocating, canMarkPackEligible, packAwb, packPartner, selectedLines, selectedOrderId]);

  const openProcessPoModal = useCallback(() => {
    if (!canProcessByPoEligible) return;
    const defaultSelected = processPoEligibleLines
      .filter((line) => !line.inStockEligible)
      .map((line) => line.sku);
    // If every line is in-stock eligible, keep all selected so user can still proceed intentionally.
    setProcessPoSelectedSkus(defaultSelected.length > 0 ? defaultSelected : processPoEligibleLines.map((line) => line.sku));
    setProcessPoModalOpen(true);
  }, [canProcessByPoEligible, processPoEligibleLines]);

  const selectOnlyNotInStockForPo = useCallback(() => {
    const list = processPoEligibleLines.filter((line) => !line.inStockEligible).map((line) => line.sku);
    setProcessPoSelectedSkus(list);
  }, [processPoEligibleLines]);

  const selectAllForPo = useCallback(() => {
    setProcessPoSelectedSkus(processPoEligibleLines.map((line) => line.sku));
  }, [processPoEligibleLines]);

  useEffect(() => {
    if (!pendingOpenPoModalOrderId) return;
    if (selectedOrderId !== pendingOpenPoModalOrderId) return;
    if (detailLoading || detailEligibilityLoading) return;
    if (!canProcessByPoEligible) {
      setDetailMsg('Process by PO not eligible for this order.');
      setPendingOpenPoModalOrderId('');
      return;
    }
    setPendingOpenPoModalOrderId('');
    openProcessPoModal();
  }, [
    canProcessByPoEligible,
    detailEligibilityLoading,
    detailLoading,
    openProcessPoModal,
    pendingOpenPoModalOrderId,
    selectedOrderId,
  ]);

  const toggleProcessPoSku = useCallback((sku) => {
    setProcessPoSelectedSkus((prev) => {
      if (prev.includes(sku)) return prev.filter((x) => x !== sku);
      return [...prev, sku];
    });
  }, []);

  const submitProcessPo = useCallback(async () => {
    if (!selectedOrderId) return;
    const selected = processPoEligibleLines.filter((line) => processPoSelectedSkus.includes(line.sku));
    if (selected.length === 0) {
      setDetailMsg('Select at least one line item for Process by PO.');
      return;
    }
    setProcessPoSubmitting(true);
    const ok = await callAction(`/api/v9/orders/${encodeURIComponent(selectedOrderId)}/process-by-po`, {
      items: selected.map((line) => ({ sku: line.sku, qty: line.qty })),
    }, 'Process by PO submitted.');
    if (ok) {
      setProcessPoModalOpen(false);
      setProcessPoSelectedSkus([]);
    }
    setProcessPoSubmitting(false);
  }, [callAction, processPoEligibleLines, processPoSelectedSkus, selectedOrderId]);

  const refreshInvoiceFromZoho = useCallback(async () => {
    if (!selectedOrderId || invoiceBusy) return;
    setInvoiceBusy(true);
    try {
      await callAction(
        `/api/v9/orders/${encodeURIComponent(selectedOrderId)}/invoice/refresh`,
        {},
        'Invoice refreshed from Zoho.'
      );
    } finally {
      setInvoiceBusy(false);
    }
  }, [callAction, invoiceBusy, selectedOrderId]);

  const acknowledgeInvoiceMismatch = useCallback(async () => {
    if (!selectedOrderId || !invoiceMismatchOpen || invoiceBusy) return;
    const noteInput = window.prompt('Optional note for invoice acknowledgement:') || '';
    const note = String(noteInput).trim();
    setInvoiceBusy(true);
    try {
      await callAction(
        `/api/v9/orders/${encodeURIComponent(selectedOrderId)}/invoice/acknowledge`,
        { note },
        'Invoice review acknowledged.'
      );
    } finally {
      setInvoiceBusy(false);
    }
  }, [callAction, invoiceBusy, invoiceMismatchOpen, selectedOrderId]);

  const resolveInvoiceReview = useCallback(async () => {
    if (!selectedOrderId || invoiceBusy || !invoiceResolveEligible) return;
    const noteInput = window.prompt('Optional note for invoice resolution:') || '';
    const note = String(noteInput).trim();
    setInvoiceBusy(true);
    try {
      await callAction(
        `/api/v9/orders/${encodeURIComponent(selectedOrderId)}/invoice/resolve`,
        { note },
        'Invoice review resolved.'
      );
    } finally {
      setInvoiceBusy(false);
    }
  }, [callAction, invoiceBusy, invoiceResolveEligible, selectedOrderId]);

  const openProcessPoFromMode = useCallback(() => {
    if (!canProcessByPoEligible) {
      setDetailMsg('Process by PO not eligible for this order.');
      return;
    }
    openProcessPoModal();
  }, [canProcessByPoEligible, openProcessPoModal]);



  const getDisplayOrderNumber = useCallback((row) => {
    return String(
      row?.sourceLinks?.shopify?.orderName ||
      row?.sourceLinks?.zoho?.referenceNumber ||
      row?.externalOrderId ||
      row?.id ||
      '-'
    ).trim() || '-';
  }, []);

  const toggleSort = useCallback((key) => {
    setSortBy((prevBy) => {
      if (prevBy === key) {
        setSortDir((prevDir) => (prevDir === 'asc' ? 'desc' : 'asc'));
        return prevBy;
      }
      setSortDir(key === 'updatedAt' ? 'desc' : 'asc');
      return key;
    });
  }, []);

  const sortedRows = useMemo(() => {
    const out = [...rows];
    const direction = sortDir === 'asc' ? 1 : -1;
    out.sort((a, b) => {
      if (sortBy === 'orderNumber') {
        const avRaw = getDisplayOrderNumber(a);
        const bvRaw = getDisplayOrderNumber(b);
        const avNum = Number(avRaw);
        const bvNum = Number(bvRaw);
        if (Number.isFinite(avNum) && Number.isFinite(bvNum)) return (avNum - bvNum) * direction;
        return avRaw.localeCompare(bvRaw, undefined, { numeric: true, sensitivity: 'base' }) * direction;
      }
      if (sortBy === 'status') {
        const av = String(a?.orderStatus || '').trim().toUpperCase();
        const bv = String(b?.orderStatus || '').trim().toUpperCase();
        return av.localeCompare(bv, undefined, { numeric: true, sensitivity: 'base' }) * direction;
      }
      if (sortBy === 'updatedAt') {
        const av = new Date(a?.updatedAt || 0).getTime();
        const bv = new Date(b?.updatedAt || 0).getTime();
        return ((Number.isFinite(av) ? av : 0) - (Number.isFinite(bv) ? bv : 0)) * direction;
      }
      return 0;
    });
    return out;
  }, [getDisplayOrderNumber, rows, sortBy, sortDir]);

  const visibleRows = sortedRows;

  // ── Bulk select helpers (after visibleRows to avoid TDZ) ─────────────────
  const toggleBulkSelectMode = useCallback(() => {
    setBulkSelectMode((prev) => {
      if (prev) setSelectedOrderIds(new Set());
      setBulkRefreshResults(null);
      return !prev;
    });
  }, []);

  const toggleRowSelection = useCallback((e, orderId) => {
    e.stopPropagation();
    setSelectedOrderIds((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId); else next.add(orderId);
      return next;
    });
  }, []);

  const selectAllVisible = useCallback(() => {
    setSelectedOrderIds(new Set(visibleRows.map((r) => r.id)));
  }, [visibleRows]);

  const clearSelection = useCallback(() => {
    setSelectedOrderIds(new Set());
    setBulkRefreshResults(null);
  }, []);

  const bulkRefreshInvoices = useCallback(async () => {
    if (bulkRefreshRunning || selectedOrderIds.size === 0) return;
    const ids = Array.from(selectedOrderIds);
    setBulkRefreshRunning(true);
    setBulkRefreshResults(null);
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch('/api/v9/orders/invoice/bulk-refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ orderIds: ids }),
      });
      const data = await res.json();
      if (!data.ok) {
        setMsg(data?.error?.message || 'Bulk refresh failed.');
      } else {
        setMsg(`Bulk refresh done: ${data.successCount}/${data.total} succeeded.`);
        setBulkRefreshResults(data.results || []);
        await loadFirstPage({ preferCache: false, silent: true });
      }
    } catch (err) {
      setMsg('Bulk refresh error: ' + (err?.message || String(err)));
    } finally {
      setBulkRefreshRunning(false);
    }
  }, [bulkRefreshRunning, currentUser, loadFirstPage, selectedOrderIds]);

  const totalAmount = useMemo(() => {
    if (visibleRows.length === rows.length) return totalAmountAllRows;
    return visibleRows.reduce((sum, row) => sum + Number(row?.amounts?.netCollectible || 0), 0);
  }, [rows.length, totalAmountAllRows, visibleRows]);

  const toggleRowAccordion = useCallback(async (orderId) => {
    if (selectedOrderId === orderId) {
      setSelectedOrderId('');
      detailRequestSeqRef.current += 1;
      resetDetailState();
      setDebugOpen(false);
      return;
    }
    setDebugOpen(false);
    await loadOrderDetail(orderId);
  }, [loadOrderDetail, resetDetailState, selectedOrderId]);

  const applyStatusPill = useCallback((status) => {
    const next = String(status || 'ALL').trim().toUpperCase() || 'ALL';
    setDraftStatus(next);
    setDraftWarningType('ALL');
    setApplied((prev) => ({
      ...prev,
      status: next,
      warningType: 'ALL',
    }));
  }, []);

  const getStatusCount = useCallback((statusValue) => {
    const key = String(statusValue || '').trim().toUpperCase();
    if (key === 'ALL') return Number(statusTotalCount || 0);
    return Number(statusCounts?.[key] || 0);
  }, [statusCounts, statusTotalCount]);

  const visibleStatusPills = useMemo(
    () => statusPills.filter((pill) => pill.value === 'ALL' || pill.value === 'CANCELLED' || pill.value === 'SALES_ORDER_PENDING' || pill.value === 'INVOICE_PENDING' || pill.value === 'INVOICE_REVIEW' || pill.value === 'WARNINGS' || getStatusCount(pill.value) > 0 || applied.status === pill.value),
    [applied.status, getStatusCount, statusPills]
  );

  const renderUnmappedShipmentNotice = () => {
    const total = Number(unmappedShipmentNotice?.totalUnmapped || 0);
    const items = Array.isArray(unmappedShipmentNotice?.items) ? unmappedShipmentNotice.items : [];
    const statusPairs = Array.isArray(unmappedShipmentNotice?.statusPairs) ? unmappedShipmentNotice.statusPairs : [];
    if (!unmappedShipmentNotice?.loaded && !unmappedShipmentNotice?.error) return null;
    if (!total && !unmappedShipmentNotice.error) return null;
    return (
      <section className="v9orders-unmapped-notice" role="status" aria-live="polite">
        <div className="v9orders-unmapped-head">
          <strong>Shipment status mapping required</strong>
          <span>{unmappedShipmentNotice.loading ? 'Refreshing...' : `${total} unmapped event(s)`}</span>
        </div>
        {!!unmappedShipmentNotice.error && (
          <div className="v9orders-unmapped-error">{unmappedShipmentNotice.error}</div>
        )}
        {!unmappedShipmentNotice.error && statusPairs.length > 0 && (
          <div className="v9orders-unmapped-chips">
            {statusPairs.slice(0, 5).map((pair) => (
              <span key={`${pair.provider}_${pair.externalStatus}`} className="v9orders-unmapped-chip">
                {String(pair.provider || '').toUpperCase()}: {pair.externalStatus} ({pair.count})
              </span>
            ))}
          </div>
        )}
        {!unmappedShipmentNotice.error && items.length > 0 && (
          <div className="v9orders-unmapped-list">
            {items.slice(0, 3).map((row) => (
              <div key={row.eventId || `${row.matchedOrderId}_${row.externalStatus}_${row.eventTime}`} className="v9orders-unmapped-item">
                Order {row.orderNumber || row.matchedOrderId} | {String(row.provider || '').toUpperCase()} | {row.externalStatus} | AWB {row.awb || '-'}
              </div>
            ))}
          </div>
        )}
        <div className="v9orders-unmapped-actions">
          <button type="button" onClick={copyUnmappedShipmentDetails} disabled={!items.length}>Copy details</button>
          {canPerformAction('admin.systemActions') ? <button type="button" onClick={openV9MappingPage}>Open V9 Mapping</button> : null}
          <button type="button" onClick={() => loadUnmappedShipmentNotice({ force: true })}>Refresh</button>
        </div>
      </section>
    );
  };

  return (
    <div className="v9orders-page">
      <header className="v9o-header">
        <div className="v9o-header-left">
          <h1>Orders</h1>
          <span className="v9o-header-count">{statusTotalCount} total</span>
          {lastUpdatedLabel && <span className="v9o-header-updated">{lastUpdatedLabel}</span>}
        </div>
        {msg && <div className="v9o-header-status">{msg}</div>}
      </header>

      {renderUnmappedShipmentNotice()}

      <section className="v9o-pillbar">
        {visibleStatusPills.map((pill) => (
          <button
            key={pill.value}
            type="button"
            className={`v9o-pill ${applied.status === pill.value ? 'active' : ''}`}
            onClick={() => applyStatusPill(pill.value)}
            disabled={loading}
          >
            {pill.label}
            <span className="v9o-pill-count">{getStatusCount(pill.value)}</span>
          </button>
        ))}
      </section>

      {applied.status === 'WARNINGS' && (
        <div className="v9o-sub-pills-wrap" style={{ marginTop: '0.4rem', display: 'flex', gap: '6px', flexWrap: 'wrap', borderTop: '1px dashed var(--v9-border)', paddingTop: '8px', alignItems: 'center' }}>
          <span style={{ fontSize: '0.8rem', color: 'var(--v9-text-secondary)', fontWeight: '600', marginRight: '4px' }}>Warning Type:</span>
          {WARNING_SUB_PILLS.map((subPill) => (
            <button
              key={subPill.value}
              type="button"
              className={`v9o-pill ${draftWarningType === subPill.value ? 'active' : ''}`}
              style={{ height: '28px', fontSize: '0.76rem', padding: '0 10px' }}
              onClick={() => {
                setDraftWarningType(subPill.value);
                setApplied((prev) => ({ ...prev, warningType: subPill.value }));
              }}
            >
              {subPill.label}
            </button>
          ))}
        </div>
      )}
      <section className="v9o-toolbar">
        <div className="v9o-toolbar-search">
          <select
            className="v9o-search-field-select"
            value={draftSearchField}
            onChange={(e) => {
              const field = e.target.value;
              setDraftSearchField(field);
              if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
              const trimmed = draftSearch.trim();
              if (trimmed.length >= 3) {
                searchDebounceRef.current = setTimeout(() => {
                  setApplied((prev) => ({ ...prev, search: trimmed, searchField: field }));
                }, 500);
              }
            }}
          >
            <option value="orderNumber">Order Number</option>
          </select>
          <input
            placeholder="Search (min 3 chars)…"
            value={draftSearch}
            onChange={(e) => {
              const val = e.target.value;
              setDraftSearch(val);
              if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
              const trimmed = val.trim();
              if (trimmed.length === 0) {
                setApplied((prev) => ({ ...prev, search: '', searchField: draftSearchField }));
                return;
              }
              if (trimmed.length < 3) return;
              searchDebounceRef.current = setTimeout(() => {
                setApplied((prev) => ({ ...prev, search: trimmed, searchField: draftSearchField }));
              }, 500);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
                setApplied({
                  status: draftStatus,
                  processingMode: draftProcessingMode,
                  search: draftSearch.trim(),
                  searchField: draftSearchField,
                  includeClosed: draftIncludeClosed,
                  warningType: draftWarningType,
                });
              }
            }}
          />
        </div>
        <div className="v9o-toolbar-filters">
          {canPerformAction('admin.systemActions') && (
            <button
              className="v9o-toolbar-btn"
              onClick={syncLinkedZohoSalesOrders}
              disabled={loading || linkedSoSyncBusy || resolveSoPendingBusy}
              title="Fetch latest Zoho Sales Order details for linked v9 orders"
            >
              {linkedSoSyncBusy ? 'Syncing Linked SO...' : 'Sync Linked SO'}
            </button>
          )}
          {canPerformAction('admin.systemActions') && (
            <button
              className="v9o-toolbar-btn"
              onClick={resolvePendingZohoSalesOrders}
              disabled={loading || linkedSoSyncBusy || resolveSoPendingBusy}
              title="Link SO Pending orders to existing Zoho SO by exact reference (no new SO creation)"
            >
              {resolveSoPendingBusy ? 'Resolving SO Pending...' : 'Resolve SO Pending'}
            </button>
          )}
          <select value={draftProcessingMode} onChange={(e) => setDraftProcessingMode(e.target.value)}>
            <option value="ALL">All Modes</option>
            <option value="NORMAL">Normal</option>
            <option value="PO">PO</option>
          </select>
          <label className="v9o-toolbar-check">
            <input type="checkbox" checked={draftIncludeClosed} onChange={(e) => setDraftIncludeClosed(e.target.checked)} />
            <span>Closed</span>
          </label>
          <button
            className="v9o-toolbar-btn primary"
            onClick={() => {
              if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
              setApplied({
                status: draftStatus,
                processingMode: draftProcessingMode,
                search: draftSearch.trim(),
                searchField: draftSearchField,
                includeClosed: draftIncludeClosed,
                warningType: draftWarningType,
              });
            }}
            disabled={loading}
          >
            Apply
          </button>
          <button
            className="v9o-toolbar-btn"
            onClick={() => {
              if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
              setDraftSearch('');
              setDraftSearchField('orderNumber');
              setDraftStatus('ALL');
              setDraftWarningType('ALL');
              setDraftProcessingMode('ALL');
              setDraftIncludeClosed(false);
              setApplied({
                status: 'ALL',
                processingMode: 'ALL',
                search: '',
                searchField: 'orderNumber',
                includeClosed: false,
                warningType: 'ALL',
              });
            }}
            disabled={loading}
          >
            Reset
          </button>
          <button
            className="v9o-toolbar-btn v9o-refresh-btn"
            onClick={() => {
              loadFirstPage({ preferCache: false, silent: false });
              loadStatusCounts();
            }}
            disabled={loading}
            title="Refresh orders list"
          >
            <span className={`v9o-refresh-icon${loading ? ' spinning' : ''}`}>↻</span>
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </section>

      <div className="v9o-stats-bar">
        <span>{visibleRows.length} orders shown</span>
        <span className="v9o-stats-sep" />
        <span>Net: ₹{formatCurrency(totalAmount)}</span>
        <span className="v9o-stats-sep" />
        <button
          type="button"
          className={`v9o-action-btn sm ${bulkSelectMode ? 'primary' : ''}`}
          onClick={toggleBulkSelectMode}
        >
          {bulkSelectMode ? `✓ Selecting (${selectedOrderIds.size})` : '☐ Select'}
        </button>
      </div>

      {/* Bulk action bar */}
      {bulkSelectMode && (
        <div className="v9o-bulk-bar">
          <span className="v9o-bulk-count">{selectedOrderIds.size} selected</span>
          <button type="button" className="v9o-action-btn sm" onClick={selectAllVisible} disabled={bulkRefreshRunning}>
            Select All ({visibleRows.length})
          </button>
          <button type="button" className="v9o-action-btn sm ghost" onClick={clearSelection} disabled={bulkRefreshRunning || selectedOrderIds.size === 0}>
            Clear
          </button>
          {canPerformAction('v9ActionQueue.invoiceRefresh') && (
            <button
              type="button"
              className="v9o-action-btn sm primary"
              onClick={bulkRefreshInvoices}
              disabled={bulkRefreshRunning || selectedOrderIds.size === 0}
            >
              {bulkRefreshRunning ? `Refreshing… (${selectedOrderIds.size})` : `Refresh Invoices (${selectedOrderIds.size})`}
            </button>
          )}
          {bulkRefreshResults && (
            <span className="v9o-bulk-results">
              {bulkRefreshResults.filter((r) => r.ok).length}/{bulkRefreshResults.length} refreshed
              {bulkRefreshResults.some((r) => !r.ok) && (
                <span className="v9o-bulk-errors"> · {bulkRefreshResults.filter((r) => !r.ok).map((r) => r.orderId).join(', ')} failed</span>
              )}
            </span>
          )}
        </div>
      )}

      <section className={`v9o-list${bulkSelectMode ? ' v9o-list--bulk' : ''}`}>
        {/* Desktop table header */}
        <div className="v9o-list-header">
          {bulkSelectMode && (
            <div className="v9o-lh-cell v9o-lh-check">
              <input
                type="checkbox"
                title="Select all visible"
                checked={visibleRows.length > 0 && visibleRows.every((r) => selectedOrderIds.has(r.id))}
                onChange={(e) => e.target.checked ? selectAllVisible() : clearSelection()}
              />
            </div>
          )}
          <div className="v9o-lh-cell v9o-lh-order v9o-sortable" onClick={() => toggleSort('orderNumber')}>
            Order {sortBy === 'orderNumber' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
          </div>
          <div className="v9o-lh-cell v9o-lh-status v9o-sortable" onClick={() => toggleSort('status')}>
            Status {sortBy === 'status' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
          </div>
          <div className="v9o-lh-cell v9o-lh-payment">Payment</div>
          <div className="v9o-lh-cell v9o-lh-amount">Amount</div>
          <div className="v9o-lh-cell v9o-lh-updated v9o-sortable" onClick={() => toggleSort('updatedAt')}>
            Updated {sortBy === 'updatedAt' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
          </div>
          <div className="v9o-lh-cell v9o-lh-actions" />
        </div>

        {visibleRows.map((row) => {
          const hasWarning = row?.flags?.hasOpenStaleWarning === true || Number(row?.flags?.staleWarningCount || 0) > 0;
          return (
            <React.Fragment key={row.id}>
              {/* Order row */}
              <div
                className={`v9o-row ${selectedOrderId === row.id ? 'active' : ''} ${bulkSelectMode && selectedOrderIds.has(row.id) ? 'bulk-selected' : ''} ${hasWarning ? 'has-warning' : ''}`}
                onClick={() => toggleRowAccordion(row.id)}
              >
                {bulkSelectMode && (
                  <div className="v9o-row-cell v9o-rc-check" onClick={(e) => toggleRowSelection(e, row.id)}>
                    <input
                      type="checkbox"
                      checked={selectedOrderIds.has(row.id)}
                      onChange={() => {}}
                      onClick={(e) => toggleRowSelection(e, row.id)}
                    />
                  </div>
                )}
                <div className="v9o-row-cell v9o-rc-order">
                  <div className="v9o-order-num">{getDisplayOrderNumber(row)}</div>
                  <div className="v9o-order-meta">
                    {String(row.processingMode || '').toUpperCase() === 'PO' && <span className="v9o-mode-tag po">PO</span>}
                    {row.poStatus && row.poStatus !== 'NOT_PO' && <span className="v9o-po-tag">{formatStatusLabel(row.poStatus)}</span>}
                  </div>
                </div>
                <div className="v9o-row-cell v9o-rc-status">
                  {hasWarning && (
                    <span className="v9o-warning-badge" style={{ marginRight: '6px' }} title="Order has active stale warning">
                      ⚠️ Warning
                    </span>
                  )}
                  <span className={`v9o-status-badge ${orderStatusTone(row.orderStatus)}`}>
                    {formatStatusLabel(row.orderStatus)}
                  </span>
                </div>
                <div className="v9o-row-cell v9o-rc-payment">
                  <span className="v9o-cell-label">Payment</span>
                  {row.paymentMode || '-'}
              </div>
              <div className="v9o-row-cell v9o-rc-amount">
                <span className="v9o-cell-label">Amount</span>
                ₹{formatCurrency(row?.amounts?.netCollectible || 0)}
              </div>
              <div className="v9o-row-cell v9o-rc-updated">
                <span className="v9o-cell-label">Updated</span>
                {row.updatedAt ? new Date(row.updatedAt).toLocaleString() : '-'}
              </div>
              <div className="v9o-row-cell v9o-rc-actions">
                {(() => {
                  const canProcessPo = (canPerformAction('orders.processPO') || canPerformAction('v9Orders.processPO')) && !!row?.processPoEligible;
                  const canUndoPo = canPerformAction('orders.undoProcessPO') && !!row?.undoProcessPoEligible;
                  const hasZohoSo = !!String(row?.sourceLinks?.zoho?.salesOrderId || '').trim();
                  const canCreateZohoSo = canPerformAction('admin.systemActions') && !hasZohoSo;
                  const canDeleteOrder = canPerformAction('orders.deleteV9Order');
                  const needsKebab = canProcessPo || canUndoPo || canDeleteOrder;
                  if (!canProcessPo && !canUndoPo && !canCreateZohoSo && !canDeleteOrder) return null;
                  return (
                    <div className="v9o-row-actions-inline">
                      {canCreateZohoSo && (
                        <button
                          type="button"
                          className="v9o-action-btn sm"
                          onClick={async (e) => {
                            e.stopPropagation();
                            const ok = window.confirm(`Create Zoho Sales Order for ${getDisplayOrderNumber(row)}?`);
                            if (!ok) return;
                            await callOrderRowAction(
                              String(row.id || '').trim(),
                              `/api/v9/orders/${encodeURIComponent(String(row.id || '').trim())}/zoho/create-sales-order`,
                              {},
                              'Zoho sales order created/linked.'
                            );
                          }}
                        >
                          Create Zoho SO
                        </button>
                      )}
                      {needsKebab && (
                        <div className="v9o-kebab-wrap">
                          <button
                            className="v9o-kebab-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActionMenuOrderId((prev) => (prev === row.id ? '' : row.id));
                            }}
                            aria-label="Open actions menu"
                          >
                            &#8942;
                          </button>
                          {actionMenuOrderId === row.id && (
                            <div className="v9o-kebab-menu" onClick={(e) => e.stopPropagation()}>
                              {canProcessPo && (
                                <button
                                  className="v9o-kebab-item"
                                  onClick={async () => {
                                    setActionMenuOrderId('');
                                    setPendingOpenPoModalOrderId(String(row.id || '').trim());
                                    if (selectedOrderId === row.id) {
                                      if (!canProcessByPoEligible) {
                                        setDetailMsg('Process by PO not eligible for this order.');
                                        return;
                                      }
                                      openProcessPoModal();
                                      return;
                                    }
                                    await loadOrderDetail(row.id);
                                  }}
                                >
                                  Process by PO
                                </button>
                              )}
                              {canUndoPo && (
                                <button
                                  className="v9o-kebab-item"
                                  onClick={async () => {
                                    setActionMenuOrderId('');
                                    const ok = window.confirm(`Undo Process by PO for order ${getDisplayOrderNumber(row)}?`);
                                    if (!ok) return;
                                    await callAction(`/api/v9/orders/${encodeURIComponent(String(row.id || '').trim())}/undo-process-po`, {}, 'Process by PO undone.');
                                  }}
                                >
                                  Undo Process PO
                                </button>
                              )}
                              {canDeleteOrder && (
                                <button
                                  className="v9o-kebab-item v9o-kebab-item--danger"
                                  onClick={async () => {
                                    setActionMenuOrderId('');
                                    const orderNum = getDisplayOrderNumber(row);
                                    const ok = window.confirm(`DELETE order ${orderNum} from V9?\n\nThis will permanently delete the order, all its lines, action-queue docs, and batch allocations. This cannot be undone.`);
                                    if (!ok) return;
                                    const token = await currentUser.getIdToken();
                                    const res = await fetch(`/api/v9/orders/${encodeURIComponent(String(row.id || '').trim())}`, {
                                      method: 'DELETE',
                                      headers: { Authorization: `Bearer ${token}` },
                                    });
                                    const data = await res.json().catch(() => null);
                                    if (!res.ok || !data?.ok) {
                                      alert(data?.error?.message || 'Delete failed.');
                                      return;
                                    }
                                    setMsg(`Order ${orderNum} deleted.`);
                                    loadFirstPage();
                                  }}
                                >
                                  Delete Order
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
            {selectedOrderId === row.id && (
              <div className="v9o-detail-panel">
                <div className="v9o-detail-card">
                  {(detailLoading || detailEligibilityLoading) && (
                    <div className="v9o-loader" aria-hidden="true"><div className="v9o-loader-bar" /></div>
                  )}
                  {!detailLoading && detailMsg && (
                    <div className={`v9o-toast ${detailMsgTone}`}>{detailMsg}</div>
                  )}
                  {selectedOrder && (
                    <>
                      {/* ── Order Info Grid ── */}
                      <div className="v9o-info-sections">
                        {/* Status & Processing */}
                        <div className="v9o-info-block">
                          <div className="v9o-info-label">Status</div>
                          <span className={`v9o-status-badge lg ${orderStatusTone(selectedOrder.orderStatus)}`}>
                            {formatStatusLabel(selectedOrder.orderStatus)}
                          </span>
                        </div>
                        <div className="v9o-info-block">
                          <div className="v9o-info-label">Mode</div>
                          <div className="v9o-mode-cell">
                            <span className={`v9o-mode-pill ${String(selectedOrder?.processingMode || 'NORMAL').toUpperCase() === 'PO' ? 'po' : 'normal'}`}>
                              {String(selectedOrder?.processingMode || 'NORMAL').toUpperCase()}
                            </span>
                            {(canPerformAction('orders.processPO') || canPerformAction('v9Orders.processPO')) && isPreShipOrderStatus(selectedOrder?.orderStatus) && (
                              <button
                                type="button"
                                className="v9o-mode-toggle"
                                onClick={openProcessPoFromMode}
                                disabled={detailLoading || detailEligibilityLoading || processPoSubmitting || !canProcessByPoEligible}
                              >
                                {processPoSubmitting ? '...' : 'Process by PO'}
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="v9o-info-block">
                          <div className="v9o-info-label">Payment</div>
                          <div className="v9o-info-value">{selectedOrder.paymentMode || '-'}</div>
                        </div>
                        <div className="v9o-info-block">
                          <div className="v9o-info-label">PO Status</div>
                          <div className="v9o-info-value">{formatStatusLabel(selectedOrder.poStatus || 'NOT_PO')}</div>
                        </div>

                        {/* Amounts */}
                        <div className="v9o-info-block">
                          <div className="v9o-info-label">Subtotal</div>
                          <div className="v9o-info-value mono">₹{formatCurrency(selectedOrder?.amounts?.subtotal || selectedOrder?.amounts?.itemTotal || 0)}</div>
                        </div>
                        <div className="v9o-info-block">
                          <div className="v9o-info-label">Shipping</div>
                          <div className="v9o-info-value mono">₹{formatCurrency(selectedOrder?.amounts?.shipping || 0)}</div>
                        </div>
                        <div className="v9o-info-block">
                          <div className="v9o-info-label">Discount</div>
                          <div className="v9o-info-value mono">₹{formatCurrency(selectedOrder?.amounts?.discount || 0)}</div>
                        </div>
                        <div className="v9o-info-block highlight">
                          <div className="v9o-info-label">Net Collectible</div>
                          <div className="v9o-info-value mono bold">₹{formatCurrency(selectedOrder?.amounts?.netCollectible || 0)}</div>
                        </div>

                        {/* Customer */}
                        {(selectedOrder?.customer?.name || selectedOrder?.customer?.phone || selectedOrder?.shippingAddress?.city) && (
                          <>
                            <div className="v9o-info-block wide">
                              <div className="v9o-info-label">Customer</div>
                              <div className="v9o-info-value">
                                {selectedOrder?.customer?.name || selectedOrder?.shippingAddress?.name || '-'}
                                {selectedOrder?.customer?.phone ? ` | ${selectedOrder.customer.phone}` : ''}
                              </div>
                            </div>
                            {(selectedOrder?.shippingAddress?.city || selectedOrder?.shippingAddress?.state) && (
                              <div className="v9o-info-block wide">
                                <div className="v9o-info-label">Ship To</div>
                                <div className="v9o-info-value">
                                  {[selectedOrder?.shippingAddress?.city, selectedOrder?.shippingAddress?.state, selectedOrder?.shippingAddress?.pincode].filter(Boolean).join(', ') || '-'}
                                </div>
                              </div>
                            )}
                          </>
                        )}

                        {/* Shipment / Packages */}
                        {(() => {
                          const orderShipments = Array.isArray(selectedOrder?.shipments) ? selectedOrder.shipments : [];
                          const legacyShip = selectedOrder?.shipment;
                          const showShipments = orderShipments.length > 0
                            ? orderShipments
                            : (legacyShip?.awb ? [{
                                awb: legacyShip.awb,
                                partner: legacyShip.partner || 'Other',
                                status: legacyShip.status || selectedOrder.orderStatus,
                                externalStatus: legacyShip.externalStatus,
                              }] : []);

                          if (showShipments.length === 0) return null;

                          return (
                            <div className="v9o-info-block wide v9o-packages-block">
                              <div className="v9o-info-label">Packages / Shipments</div>
                              <div className="v9o-packages-list" style={{ marginTop: '8px' }}>
                                {showShipments.map((pkg, idx) => (
                                  <div className="v9o-package-item" key={pkg.awb || idx} style={{ borderBottom: '1px solid rgba(0,0,0,0.05)', paddingBottom: '8px', marginBottom: '8px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <div>
                                        <span style={{ fontWeight: '600' }}>Pkg {idx + 1}: </span>
                                        <span className="mono" style={{ background: '#f5f5f5', padding: '2px 6px', borderRadius: '4px', fontSize: '0.9em' }}>{pkg.awb}</span>
                                        <span style={{ marginLeft: '8px', fontSize: '0.85em', color: '#666' }}>({String(pkg.partner || 'Other').toUpperCase()})</span>
                                      </div>
                                      <span className={`v9o-status-badge sm ${orderStatusTone(pkg.status || 'shipped')}`}>
                                        {formatStatusLabel(pkg.status || 'shipped')}
                                      </span>
                                    </div>
                                    {pkg.externalStatus && (
                                      <div style={{ fontSize: '0.85em', color: '#555', marginTop: '4px' }}>
                                        <strong>Tracking:</strong> {pkg.externalStatus}
                                      </div>
                                    )}
                                    {/* Status Selector Dropdown for manual update */}
                                    {canPerformAction('orders.processReturn') && pkg.status !== 'DELIVERED' && pkg.status !== 'RTO_RECEIVED' && (
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                                        <span style={{ fontSize: '0.8em', color: '#666' }}>Update Status:</span>
                                        <select
                                          value={pkg.status || 'SHIPPED'}
                                          style={{ padding: '2px 6px', fontSize: '0.8em', borderRadius: '4px', border: '1px solid #ccc', background: '#fff' }}
                                          onChange={async (e) => {
                                            const newStatus = e.target.value;
                                            if (!newStatus) return;
                                            const remarks = window.prompt(`Update package ${pkg.awb} status to ${formatStatusLabel(newStatus)}?\nEnter optional tracking remarks:`, '');
                                            if (remarks === null) return; // cancelled
                                            await callAction(`/api/v9/orders/${encodeURIComponent(selectedOrderId)}/manual-status`, {
                                              awb: pkg.awb,
                                              status: newStatus,
                                              remarks: remarks.trim()
                                            }, `Package status updated to ${formatStatusLabel(newStatus)}.`);
                                          }}
                                        >
                                          <option value="SHIPPED">In Transit / Shipped</option>
                                          <option value="DELIVERED">Delivered</option>
                                          <option value="RTO_IN_PROGRESS">RTO In Progress</option>
                                          <option value="RTO_RECEIVED">RTO Received</option>
                                        </select>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })()}

                        {/* Source Links */}
                        {(selectedOrder?.sourceLinks?.shopify?.orderName || selectedOrder?.sourceLinks?.zoho?.salesOrderNumber) && (
                          <div className="v9o-info-block wide">
                            <div className="v9o-info-label">Source</div>
                            <div className="v9o-info-value v9o-source-links">
                              {selectedOrder?.sourceLinks?.shopify?.orderName && (
                                <span className="v9o-source-tag shopify">Shopify: {selectedOrder.sourceLinks.shopify.orderName}</span>
                              )}
                              {selectedOrder?.sourceLinks?.zoho?.salesOrderNumber && (
                                <span className="v9o-source-tag zoho">Zoho SO: {selectedOrder.sourceLinks.zoho.salesOrderNumber}</span>
                              )}
                              {selectedOrder?.sourceLinks?.zoho?.referenceNumber && (
                                <span className="v9o-source-tag zoho">Ref: {selectedOrder.sourceLinks.zoho.referenceNumber}</span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* ── Quick Actions Bar ── */}
                      <div className="v9o-actions-bar">
                        {canPerformAction('orders.markPacked') && canMarkPackEligible && (
                          <button className="v9o-action-btn primary" onClick={() => { setPackForceMode(false); setPackModalOpen(true); }}>Mark Pack</button>
                        )}
                        {canPerformAction('orders.forcePackAllocating') && canForcePackAllocating && (
                          <button className="v9o-action-btn danger" onClick={() => { setPackForceMode(true); setPackModalOpen(true); }}>Force Pack</button>
                        )}
                        {canPerformAction('orders.ship') && String(selectedOrder?.shipment?.awb || '').trim() && (
                          <>
                            <button
                              className="v9o-action-btn"
                              onClick={() => callAction('/api/v9/shipments/fetch-awb-status', {
                                provider: String(selectedOrder?.shipment?.partner || 'delhivery').toLowerCase(),
                                awb: selectedOrder?.shipment?.awb,
                              }, 'AWB refreshed.')}
                            >
                              Refresh AWB
                            </button>
                            {String(selectedOrder?.shipment?.externalStatus || '').trim() && (
                              <button
                                className="v9o-action-btn"
                                onClick={() => callAction(`/api/v9/orders/${encodeURIComponent(selectedOrderId)}/repair-shipment-status`, {}, 'Shipment status repaired.')}
                              >
                                Repair Shipment
                              </button>
                            )}
                          </>
                        )}
                        {canPerformAction('orders.processReturn') && canMarkRtoReceived && (
                          <button className="v9o-action-btn warn" onClick={() => callAction(`/api/v9/orders/${encodeURIComponent(selectedOrderId)}/rto-received`, {}, 'RTO marked received.')}>Mark RTO Received</button>
                        )}
                        {canPerformAction('batches.delete') && selectedOrder?.batchId && (
                          <button
                            className="v9o-action-btn danger"
                            onClick={() => callDeleteAction(`/api/v9/production-batches/${encodeURIComponent(selectedOrder.batchId)}`, 'Batch deleted with auto-undo.')}
                          >
                            Delete Batch
                          </button>
                        )}
                        {canPerformAction('admin.systemActions') && (
                          <button className="v9o-action-btn ghost" onClick={() => setDebugOpen((prev) => !prev)}>
                            {debugOpen ? 'Hide Debug' : 'Debug'}
                          </button>
                        )}
                        {(() => {
                          const hiddenLineCount = selectedLines.filter((line) => {
                            const req = Number(line?.netRequiredQty || 0);
                            const rec = getComparableReceivedQty(line);
                            const rstk = Math.max(0, Number(line?.restockPendingQty || 0) - Number(line?.restockedQty || 0));
                            const inBatch = getComparableInBatchQty(line);
                            const packed = Number(line?.packedQty || 0);
                            const shipped = Number(line?.shippedQty || 0);
                            const returned = Number(line?.returnedQty || 0);
                            const restocked = Number(line?.restockedQty || 0);
                            return !(req > 0 || rec > 0 || rstk > 0 || inBatch > 0 || packed > 0 || shipped > 0 || returned > 0 || restocked > 0);
                          }).length;
                          return hiddenLineCount > 0 ? (
                            <button className="v9o-action-btn ghost" onClick={() => setShowHiddenLines((prev) => !prev)}>
                              {showHiddenLines ? `Hide Hidden (${hiddenLineCount})` : `Show Hidden (${hiddenLineCount})`}
                            </button>
                          ) : null;
                        })()}
                      </div>

                      {((detailWarningsLoading && selectedOrder?.flags?.hasOpenStaleWarning) || detailWarnings.length > 0) && (
                        <section className="v9o-warning-panel">
                          <div className="v9o-section-head">
                            <strong>Warning</strong>
                            <span className="v9o-warning-count">{detailWarningsLoading ? 'Checking' : detailWarnings.length}</span>
                          </div>
                          {detailWarnings.length > 0 ? (
                            <div className="v9o-warning-list">
                            {detailWarnings.map((warning) => {
                              const severity = String(warning?.severity || 'WARNING').trim().toUpperCase();
                              const ageHours = Number(warning?.ageHours || 0);
                              const maxHours = Number(warning?.maxHours || 0);
                              const reason = String(warning?.reason || warning?.message || '').trim() || 'Warning reason unavailable.';
                              const suggestedAction = String(warning?.suggestedAction || '').trim();
                              const externalStatus = String(warning?.externalStatus || warning?.trackingStatus || warning?.shipmentStatus || '').trim();
                              const partner = String(warning?.partner || warning?.shipmentPartner || '').trim();
                              const awb = String(warning?.awb || warning?.trackingNumber || '').trim();
                                return (
                                  <div className="v9o-warning-row" key={warning.id || `${warning.orderId}-${reason}`}>
                                    <div className="v9o-warning-main" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                                      <div>
                                        <span className={`v9o-warning-badge ${severity.toLowerCase()}`}>{severity}</span>
                                        <span className="v9o-warning-reason">{reason}</span>
                                      </div>
                                      {warning.id && !warning.resolved && (
                                        <button
                                          type="button"
                                          className="v9o-action-btn sm"
                                          style={{ padding: '2px 8px', fontSize: '0.8em', border: '1px solid #ccc', background: '#fff' }}
                                          onClick={async () => {
                                            const hoursStr = window.prompt('Enter snooze duration in hours (e.g. 24, 48, 168):', '48');
                                            if (hoursStr === null) return;
                                            const hours = parseInt(hoursStr, 10);
                                            if (isNaN(hours) || hours <= 0) {
                                              alert('Please enter a valid positive number of hours.');
                                              return;
                                            }
                                            const snoozeReason = window.prompt('Enter snooze reason (optional):', '');
                                            if (snoozeReason === null) return;
                                            await callAction(`/api/v9/admin/stale-watchlist/${encodeURIComponent(warning.id)}/snooze`, {
                                              hours,
                                              reason: snoozeReason.trim()
                                            }, 'Warning snoozed successfully.');
                                          }}
                                        >
                                          Snooze
                                        </button>
                                      )}
                                    </div>
                                    <div className="v9o-warning-meta">
                                      {Number.isFinite(ageHours) && ageHours > 0 && (
                                        <span>Age: {formatWarningAge(ageHours)}{Number.isFinite(maxHours) && maxHours > 0 ? ` / ${formatWarningAge(maxHours)}` : ''}</span>
                                      )}
                                      {suggestedAction && <span>Action: {formatStatusLabel(suggestedAction)}</span>}
                                      {awb && <span>AWB: {awb}</span>}
                                      {partner && <span>Partner: {partner.toUpperCase()}</span>}
                                      {externalStatus && <span>Tracking: {externalStatus}</span>}
                                      {(warning?.lastCheckedAt || warning?.checkedAt) && (
                                        <span>Checked: {formatDateTimeOrDash(warning.lastCheckedAt || warning.checkedAt)}</span>
                                      )}
                                    </div>
                                    {(() => {
                                       const status = String(warning?.orderStatus || '').trim().toUpperCase();
                                       let guidelineText = '';
                                       let actionButtons = [];

                                       if (status === 'IN_TRANSIT') {
                                         guidelineText = "This package has been in transit for more than 7 days. Check the courier status online, contact the customer, or contact the courier partner to escalate this.";
                                         if (awb) {
                                           const trackUrl = String(partner).toLowerCase() === 'delhivery'
                                             ? `https://www.delhivery.com/track/package/${awb}`
                                             : `https://shiprocket.co/tracking/${awb}`;
                                           actionButtons.push(
                                             <a
                                               key="track"
                                               href={trackUrl}
                                               target="_blank"
                                               rel="noreferrer"
                                               className="v9o-action-btn sm"
                                               style={{ padding: '2px 8px', fontSize: '0.8em', border: '1px solid var(--v9-primary)', color: 'var(--v9-primary)', background: '#fff', textDecoration: 'none', height: 'auto', minHeight: 'unset', display: 'inline-flex', alignItems: 'center' }}
                                             >
                                               Track Courier ↗
                                             </a>
                                           );
                                         }
                                       } else if (status === 'RTO_IN_PROGRESS') {
                                         guidelineText = "The shipment failed delivery and is returning. Check if the returned fabric has arrived at the warehouse to restock it. If it is in return transit, contact the shipping partner and escalate this.";
                                         if (awb) {
                                           const trackUrl = String(partner).toLowerCase() === 'delhivery'
                                             ? `https://www.delhivery.com/track/package/${awb}`
                                             : `https://shiprocket.co/tracking/${awb}`;
                                           actionButtons.push(
                                             <a
                                               key="track"
                                               href={trackUrl}
                                               target="_blank"
                                               rel="noreferrer"
                                               className="v9o-action-btn sm"
                                               style={{ padding: '2px 8px', fontSize: '0.8em', border: '1px solid var(--v9-primary)', color: 'var(--v9-primary)', background: '#fff', textDecoration: 'none', height: 'auto', minHeight: 'unset', display: 'inline-flex', alignItems: 'center' }}
                                              >
                                               Track RTO ↗
                                              </a>
                                            );
                                          }
                                          if (canPerformAction('orders.processReturn') && canMarkRtoReceived) {
                                            actionButtons.push(
                                              <button
                                                key="rto-received"
                                                type="button"
                                                className="v9o-action-btn sm primary"
                                                style={{ padding: '2px 8px', fontSize: '0.8em', height: 'auto', minHeight: 'unset' }}
                                                onClick={async () => {
                                                  if (!window.confirm(`Mark order as RTO received?`)) return;
                                                  const ok = await callAction(`/api/v9/orders/${encodeURIComponent(selectedOrderId)}/rto-received`, {}, 'RTO marked received.');
                                                  if (ok) {
                                                    loadOrderWarnings(selectedOrderId);
                                                  }
                                                }}
                                              >
                                                Mark RTO Received
                                              </button>
                                            );
                                          }
                                        } else if (status === 'READY_TO_PACK') {
                                          guidelineText = "Fabric is ready. Pack and ship this order.";
                                          if (canPerformAction('orders.ship') && canMarkPackEligible) {
                                            actionButtons.push(
                                              <button
                                                key="pack"
                                                type="button"
                                                className="v9o-action-btn sm primary"
                                                style={{ padding: '2px 8px', fontSize: '0.8em', height: 'auto', minHeight: 'unset' }}
                                                onClick={() => { setPackForceMode(false); setPackModalOpen(true); }}
                                              >
                                                Pack / Ship Now
                                              </button>
                                            );
                                          }
                                        } else if (status === 'PRODUCTION') {
                                          guidelineText = "Fabric is in production beyond SLA. Check the production status or contact the fabric vendor.";
                                        } else if (status === 'ALLOCATING') {
                                          guidelineText = "Fabric is not yet fully allocated. Check available inventory or allocate stock pieces.";
                                        }

                                        if (!guidelineText && actionButtons.length === 0) return null;

                                        return (
                                          <div style={{ marginTop: '8px', padding: '8px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '4px', width: '100%', boxSizing: 'border-box' }}>
                                            {guidelineText && (
                                              <div style={{ fontSize: '0.78rem', color: '#475569', marginBottom: actionButtons.length > 0 ? '6px' : '0', lineHeight: '1.4' }}>
                                                <strong>Guide:</strong> {guidelineText}
                                              </div>
                                            )}
                                            {actionButtons.length > 0 && (
                                              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '6px' }}>
                                                {actionButtons}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })()}
                                  </div>
                                );
                            })}
                            </div>
                          ) : (
                            <div className="v9o-warning-empty">Checking warning reason...</div>
                          )}
                        </section>
                      )}

                      {/* ── Invoice Panel ── */}
                      <section className="v9o-invoice-panel">
                        <div className="v9o-section-head">
                          <strong>Invoice</strong>
                          <span className={`v9o-invoice-badge ${invoiceCoverageTone(invoiceCoverageState)}`}>
                            {invoiceCoverageLabel(invoiceCoverageState)}
                          </span>
                        </div>
                        <div className="v9o-invoice-grid">
                          <div><span className="v9o-info-label">Invoice #</span><span>{String(invoiceDoc?.number || '').trim() || '-'}</span></div>
                          <div><span className="v9o-info-label">Date</span><span>{formatDateTimeOrDash(invoiceDoc?.date || invoiceDoc?.createdAt)}</span></div>
                          <div><span className="v9o-info-label">Req Qty</span><span>{formatQty(invoiceCoverage?.totalRequiredQty || 0)}</span></div>
                          <div><span className="v9o-info-label">Inv Qty</span><span>{formatQty(invoiceCoverage?.totalInvoicedQty || 0)}</span></div>
                          <div><span className="v9o-info-label">Delta</span><span className={Math.abs(Number(invoiceCoverage?.deltaQty || 0)) < 1e-6 ? '' : 'v9o-text-danger'}>{formatQty(invoiceCoverage?.deltaQty || 0)}</span></div>
                          <div><span className="v9o-info-label">Mismatches</span><span>{formatQty(invoiceCoverage?.mismatchCount || 0)}</span></div>
                        </div>
                        {invoiceReviewStatus ? (
                          <div className="v9o-invoice-note">
                            Review: {invoiceReviewStatus}
                            {invoiceReview?.updatedAt ? ` on ${formatDateTimeOrDash(invoiceReview.updatedAt)}` : ''}
                            {invoiceReview?.note ? ` - ${invoiceReview.note}` : ''}
                          </div>
                        ) : null}
                        {invoiceDisplayRows.length > 0 && (
                          <div className="v9o-invoice-lines-wrap">
                            <table className="v9o-invoice-lines">
                              <thead><tr><th>SKU</th><th>REQ</th><th>INV</th><th>DELTA</th></tr></thead>
                              <tbody>
                                {invoiceDisplayRows.map((line) => (
                                  <tr key={`inv-${line.sku}`}>
                                    <td>{line.sku}</td>
                                    <td>{formatQty(line.req)}</td>
                                    <td>{formatQty(line.inv)}</td>
                                    <td className={Math.abs(line.delta) < 1e-6 ? 'ok' : 'bad'}>{formatQty(line.delta)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                        <div className="v9o-invoice-actions">
                          {canPerformAction('orders.ship') && (
                            <button type="button" className="v9o-action-btn sm" onClick={refreshInvoiceFromZoho} disabled={invoiceBusy}>
                              {invoiceBusy ? 'Refreshing...' : 'Refresh'}
                            </button>
                          )}
                          {canPerformAction('orders.ship') && invoiceMismatchOpen && (
                            <button type="button" className="v9o-action-btn sm" onClick={acknowledgeInvoiceMismatch} disabled={invoiceBusy}>Acknowledge</button>
                          )}
                          {canPerformAction('orders.ship') && (
                            <button type="button" className="v9o-action-btn sm" onClick={resolveInvoiceReview} disabled={invoiceBusy || !invoiceResolveEligible}>Resolve</button>
                          )}
                        </div>
                      </section>

                      {/* ── Debug ── */}
                      {canPerformAction('admin.systemActions') && debugOpen && (
                        <div className="v9o-debug-wrap">
                          <div className="v9o-debug-head">
                            <strong>TEMP DEBUG VIEW</strong>
                            <div className="v9o-debug-btns">
                              <button className="v9o-action-btn sm ghost" onClick={() => copyDebugJson(selectedOrder || {}, 'Order JSON')}>Copy Order</button>
                              <button className="v9o-action-btn sm ghost" onClick={() => copyDebugJson(selectedLines || [], 'Lines JSON')}>Copy Lines</button>
                              <button className="v9o-action-btn sm ghost" onClick={() => copyDebugJson(lineEligibility || {}, 'Eligibility JSON')}>Copy Eligibility</button>
                            </div>
                          </div>
                          <div className="v9o-debug-grid">
                            <div className="v9o-debug-card"><div className="v9o-debug-title">Order Doc</div><pre>{JSON.stringify(selectedOrder || {}, null, 2)}</pre></div>
                            <div className="v9o-debug-card"><div className="v9o-debug-title">Line Docs</div><pre>{JSON.stringify(selectedLines || [], null, 2)}</pre></div>
                            <div className="v9o-debug-card"><div className="v9o-debug-title">Eligibility</div><pre>{JSON.stringify(lineEligibility || {}, null, 2)}</pre></div>
                          </div>
                        </div>
                      )}

                      {/* ── Line Items ── */}
                      <section className="v9o-lines-section">
                        <div className="v9o-section-head"><strong>Line Items</strong><span className="v9o-section-count">{selectedLines.length}</span></div>
                        <div className="v9o-lines-wrap">
                          <table className="v9o-lines-table">
                            <thead>
                              <tr>
                                <th></th>
                                <th>SKU</th>
                                <th title="Current required qty">REQ</th>
                                <th title="Cancelled / removed qty">CAN</th>
                                <th title="Received qty">REC</th>
                                <th title="Restocked / restock pending qty">RSTK</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedLines.filter((line) => {
                                if (showHiddenLines) return true;
                                const req = Number(line?.netRequiredQty || 0);
                                const rec = getComparableReceivedQty(line);
                                const rstk = Math.max(0, Number(line?.restockPendingQty || 0) - Number(line?.restockedQty || 0));
                                const inBatch = getComparableInBatchQty(line);
                                const packed = Number(line?.packedQty || 0);
                                const shipped = Number(line?.shippedQty || 0);
                                const returned = Number(line?.returnedQty || 0);
                                const restocked = Number(line?.restockedQty || 0);
                                return req > 0 || rec > 0 || rstk > 0 || inBatch > 0 || packed > 0 || shipped > 0 || returned > 0 || restocked > 0;
                              }).map((line) => {
                                const elig = lineEligibility[line.sku] || {};
                                const usedOpenQty = Number(detailData?.usedOpenBySku?.[line.sku] || 0);
                                const canUndoUsedForLine = usedOpenQty > 0;
                                const restockPendingQty = Number(line.restockPendingQty || 0);
                                const excessPendingQty = getExcessPendingQty(line);
                                const lineActivity = detailData?.lineActivityByLine?.[line.id] || {};
                                const awaitingBatchIds = Array.isArray(lineActivity?.awaitingBatchIds) ? lineActivity.awaitingBatchIds : [];
                                const receivedBatchIds = Array.isArray(lineActivity?.receivedBatchIds) ? lineActivity.receivedBatchIds : [];
                                const usedPieceIdsFromLine = Array.isArray(lineActivity?.usedPieceIds) ? lineActivity.usedPieceIds : [];
                                const usedSkuPieceMap = detailData?.usedOpenBySkuPiece?.[line.sku] || {};
                                const usedPieceIdsFromSku = Object.keys(usedSkuPieceMap)
                                  .filter((pieceId) => Number(usedSkuPieceMap[pieceId] || 0) > 1e-9);
                                const usedPieceIds = usedPieceIdsFromLine.length > 0
                                  ? usedPieceIdsFromLine
                                  : usedPieceIdsFromSku;
                                const allocationMode = String(elig?.allocationMode || line?.allocationMode || '').trim().toUpperCase();
                                const componentSummary = Array.isArray(elig?.componentSummary) ? elig.componentSummary : [];
                                const isBundleLine = line?.isBundle === true || componentSummary.length > 0 || allocationMode === 'COMPONENT_STOCK' || allocationMode === 'BUNDLE_OUT_OF_STOCK' || allocationMode === 'BUNDLE_NO_MAPPING';
                                const showBundleComponents = isBundleLine && allocationMode !== 'BUNDLE_STOCK';
                                const lineImage = String(line?.productImageUrl || line?.imageUrl || line?.image || elig?.productImageUrl || '').trim();
                                return (
                                  <React.Fragment key={line.id}>
                                    <tr>
                                      <td>
                                        {lineImage ? (
                                          <img className="v9o-line-thumb" src={lineImage} alt={line.sku} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                                        ) : (
                                          <div className="v9o-line-thumb-empty">--</div>
                                        )}
                                      </td>
                                      <td>
                                        <div className="v9o-sku-cell">
                                          <span className="v9o-sku">{line.sku}</span>
                                          {receivedBatchIds.length > 0 && (
                                            <div className="v9o-line-subtext">
                                              Received from Batch: {compactIdList(receivedBatchIds)}
                                            </div>
                                          )}
                                          {awaitingBatchIds.length > 0 && receivedBatchIds.length === 0 && (
                                            <div className="v9o-line-subtext">
                                              Awaiting in Batch: {compactIdList(awaitingBatchIds)}
                                            </div>
                                          )}
                                          {usedPieceIds.length > 0 && (
                                            <div className="v9o-line-subtext">
                                              Used from: {compactIdList(usedPieceIds)}
                                            </div>
                                          )}
                                        </div>
                                      </td>
                                      <td>{formatQtyOrBlank(line.netRequiredQty || 0)}</td>
                                      <td>{formatQtyOrBlank(line.cancelledQty || 0)}</td>
                                      <td>{formatQtyOrBlank(getComparableReceivedQty(line))}</td>
                                      <td>{restockPendingQty > 0 ? `${Number(line.restockedQty || 0)}/${restockPendingQty}` : '—'}</td>
                                      <td>
                                        <div className="v9o-line-actions">
                                          {canPerformAction('orders.markUsed') && elig.canMarkUsed && (
                                            <button className="v9o-action-btn sm primary" onClick={() => openPiecePicker(line)}>Mark Used</button>
                                          )}
                                          {canPerformAction('orders.undoUsed') && canUndoUsedAtOrderLevel && canUndoUsedForLine && (
                                            <button className="v9o-action-btn sm" onClick={() => {
                                              const pieceMap = detailData?.usedOpenBySkuPiece?.[line.sku] || {};
                                              const pieceMetaMap = detailData?.usedOpenBySkuPieceMeta?.[line.sku] || {};
                                              const pieceEntries = Object.entries(pieceMap).filter(([, q]) => Number(q || 0) > 0);
                                              if (pieceEntries.length === 0) { setDetailMsg(`No used-piece breakdown found for ${line.sku}.`); return; }
                                              const totalQty = pieceEntries.reduce((s, [, q]) => s + Number(q || 0), 0);
                                              const ok = window.confirm(`Undo used for ${line.sku} (${totalQty.toFixed(2)}m)?`);
                                              if (!ok) return;
                                              const items = pieceEntries.map(([stockPieceId, q]) => ({
                                                sku: line.sku,
                                                stockPieceId,
                                                stockSku: String(pieceMetaMap?.[stockPieceId]?.stockSku || line.sku || '').trim().toUpperCase(),
                                                qty: Number(Number(q || 0).toFixed(3)),
                                              })).filter((x) => x.qty > 0);
                                              if (items.length === 0) { setDetailMsg(`No undo quantity available for ${line.sku}.`); return; }
                                              callAction(`/api/v9/orders/${encodeURIComponent(selectedOrderId)}/undo-mark-used`, { items }, 'Undo mark used done.');
                                            }}>Undo Used</button>
                                          )}
                                          {(canPerformAction('orders.restock') || canPerformAction('orders.undoRestock'))
                                            && (((isRestockStatusEligible && (restockPendingQty - Number(line.restockedQty || 0)) > 0) || excessPendingQty > 0.0001)
                                              || Number(line.restockedQty || 0) > 0 || Number(line.excessRestockedQty || 0) > 0) && (
                                            <button className="v9o-action-btn sm" onClick={() => {
                                              const orderNumber = String(
                                                selectedOrder?.sourceLinks?.shopify?.orderName
                                                || selectedOrder?.sourceLinks?.zoho?.referenceNumber
                                                || selectedOrder?.externalOrderId
                                                || selectedOrderId
                                                || '-'
                                              ).trim() || '-';
                                              const fallbackImage = lineEligibility[String(line?.sku || '').trim().toUpperCase()]?.productImageUrl || '';
                                              setRestockPickerCtx({
                                                sku: line.sku,
                                                orderNumber,
                                                productImage: String(line?.productImageUrl || line?.imageUrl || line?.image || fallbackImage || '').trim(),
                                                productName: line?.productName || line?.name || line?.title || '',
                                              });
                                              setRestockPickerOpen(true);
                                            }}>{(((isRestockStatusEligible && (restockPendingQty - Number(line.restockedQty || 0)) > 0) || excessPendingQty > 0.0001)) ? 'Restock' : 'Undo'}</button>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                    {showBundleComponents && (
                                      <tr className="v9o-bundle-row">
                                        <td />
                                        <td colSpan="6">
                                          <div className="v9o-bundle-wrap">
                                            <div className="v9o-bundle-head"><strong>Bundle Components</strong><span className="v9o-bundle-mode">{allocationMode || 'BUNDLE'}</span></div>
                                            {componentSummary.length > 0 ? (
                                              <table className="v9o-bundle-table">
                                                <thead><tr><th>Component SKU</th><th>Required</th><th>Available</th><th>Status</th></tr></thead>
                                                <tbody>
                                                  {componentSummary.map((comp) => {
                                                    const eligible = !!comp?.eligible;
                                                    return (
                                                      <tr key={`${line.id}-${String(comp?.componentSku || '').trim().toUpperCase()}`}>
                                                        <td>{String(comp?.componentSku || '-').trim().toUpperCase()}</td>
                                                        <td>{Number(comp?.requiredQty || 0).toFixed(2)}</td>
                                                        <td>{Number(comp?.availableQty || 0).toFixed(2)}</td>
                                                        <td><span className={`v9o-stock-pill ${eligible ? 'ok' : 'short'}`}>{eligible ? 'In Stock' : 'Short'}</span></td>
                                                      </tr>
                                                    );
                                                  })}
                                                </tbody>
                                              </table>
                                            ) : (
                                              <div className="v9o-bundle-missing">Bundle mapping not found for this SKU.</div>
                                            )}
                                          </div>
                                        </td>
                                      </tr>
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </section>

                      {/* ── Activity Log ── */}
                      <section className="v9o-audit">
                        <div className="v9o-section-head">
                          <strong>Activity Log</strong>
                          {auditLogLoading && <span className="v9o-audit-loading">Loading...</span>}
                          {!auditLogLoading && auditLogItems.length > 0 && <span className="v9o-section-count">{auditLogItems.length}</span>}
                        </div>
                        {!auditLogLoading && auditLogError && <div className="v9o-audit-empty v9o-audit-error">{auditLogError}</div>}
                        {!auditLogLoading && !auditLogError && auditLogItems.length === 0 && <div className="v9o-audit-empty">No activity recorded.</div>}
                        {auditLogItems.length > 0 && (
                          <>
                            <div className="v9o-audit-list">
                              {(auditLogExpanded ? auditLogItems : auditLogItems.slice(0, 3)).map((entry) => (
                                <div key={entry.id} className="v9o-audit-entry">
                                  <div className="v9o-audit-action">{String(entry.action || '').replace(/_/g, ' ')}</div>
                                  <div className="v9o-audit-meta">
                                    <span>{(typeof entry.actor === 'object' ? (entry.actor?.name || entry.actor?.uid) : entry.actor) || '-'}</span>
                                    <span>{formatDateTimeOrDash(entry.createdAt)}</span>
                                    {entry.source ? <span className="v9o-audit-source">{entry.source}</span> : null}
                                  </div>
                                </div>
                              ))}
                            </div>
                            {auditLogItems.length > 3 && (
                              <button type="button" className="v9o-audit-toggle" onClick={() => setAuditLogExpanded((prev) => !prev)}>
                                {auditLogExpanded ? 'Show Less' : `Show More (${auditLogItems.length - 3} more)`}
                              </button>
                            )}
                          </>
                        )}
                      </section>
                    </>
                  )}

                  <MarkUsedModal
                    isOpen={piecePickerOpen && !!piecePickerLine}
                    loading={false}
                    submitting={piecePickerSubmitting}
                    context={{
                      sku: piecePickerLine?.sku || '',
                      orderNumber: piecePickerLine?.orderNumber || '',
                      orderId: piecePickerLine?.orderId || '',
                      pendingQty: Number(piecePickerLine?.pendingQty || 0),
                      orderedQty: Number(piecePickerLine?.orderedQty || 0),
                      receivedQty: Number(piecePickerLine?.receivedQty || 0),
                      productName: piecePickerLine?.productName || '',
                      productImage: piecePickerLine?.productImage || '',
                      allocationMode: piecePickerLine?.allocationMode || 'DIRECT',
                      componentSummary: Array.isArray(piecePickerLine?.componentSummary) ? piecePickerLine.componentSummary : [],
                      componentCandidates: Array.isArray(piecePickerLine?.componentCandidates) ? piecePickerLine.componentCandidates : [],
                    }}
                    pieces={piecePickerLine?.pieces || []}
                    onClose={() => { if (piecePickerSubmitting) return; setPiecePickerOpen(false); setPiecePickerLine(null); }}
                    onSubmit={confirmPiecePicker}
                  />

                  <RestockPickerModal
                    open={restockPickerOpen}
                    orderId={selectedOrderId}
                    sku={restockPickerCtx.sku}
                    orderNumber={restockPickerCtx.orderNumber}
                    productImage={restockPickerCtx.productImage}
                    productName={restockPickerCtx.productName}
                    authedFetch={authedFetch}
                    onClose={() => { setRestockPickerOpen(false); setRestockPickerCtx({ sku: '', orderNumber: '', productImage: '', productName: '' }); }}
                    onDone={async () => {
                      setDetailMsg('Restock done.');
                      if (selectedOrderId) await loadOrderDetail(selectedOrderId);
                      await loadFirstPage({ preferCache: false });
                    }}
                  />

                  {packModalOpen && (
                    <div className="v9o-modal-overlay" onClick={() => { if (!packSubmitting) { setPackModalOpen(false); setPackForceMode(false); } }}>
                      <div className="v9o-modal" onClick={(e) => e.stopPropagation()}>
                        <h3>{packForceMode ? '⚠ Force Pack (Admin Override)' : 'Mark Pack'}</h3>
                        <div className="v9o-modal-subtitle">Order: {selectedOrder?.sourceLinks?.shopify?.orderName || selectedOrder?.sourceLinks?.zoho?.referenceNumber || selectedOrderId}</div>
                        {packForceMode && (
                          <div className="v9o-modal-warning">
                            Fabric may not be fully received. Order will be packed using required quantities. This action is logged.
                          </div>
                        )}
                        <div className="v9o-modal-fields">
                          <select value={packPartner} onChange={(e) => setPackPartner(e.target.value)} disabled={packSubmitting}>
                            <option value="delhivery">Delhivery</option>
                            <option value="shiprocket">Shiprocket</option>
                          </select>
                          <input value={packAwb} onChange={(e) => setPackAwb(e.target.value)} placeholder="AWB Number" disabled={packSubmitting} />
                        </div>
                        <div className="v9o-modal-footer">
                          <button className="v9o-action-btn ghost" onClick={() => { setPackModalOpen(false); setPackForceMode(false); }} disabled={packSubmitting}>Cancel</button>
                          <button
                            className={packForceMode ? 'v9o-action-btn danger' : 'v9o-action-btn primary'}
                            onClick={() => submitPack(packForceMode)}
                            disabled={packSubmitting || !String(packAwb || '').trim() || (!packForceMode && !canMarkPackEligible)}
                          >
                            {packSubmitting ? 'Packing...' : packForceMode ? 'Confirm Force Pack' : 'Confirm Pack'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {processPoModalOpen && (
                    <div className="v9o-modal-overlay" onClick={() => { if (!processPoSubmitting) setProcessPoModalOpen(false); }}>
                      <div className="v9o-modal" onClick={(e) => e.stopPropagation()}>
                        <h3>Process by PO</h3>
                        <div className="v9o-modal-subtitle">Select line items to add in single-order PO batch.</div>
                        <div className="v9o-po-quick">
                          <button type="button" className="v9o-action-btn sm ghost" onClick={selectOnlyNotInStockForPo} disabled={processPoSubmitting}>Not In Stock Only</button>
                          <button type="button" className="v9o-action-btn sm ghost" onClick={selectAllForPo} disabled={processPoSubmitting}>Select All</button>
                        </div>
                        <div className="v9o-po-list">
                          {processPoEligibleLines.map((line) => (
                            <label key={line.sku} className="v9o-po-item">
                              <input type="checkbox" checked={processPoSelectedSkus.includes(line.sku)} onChange={() => toggleProcessPoSku(line.sku)} disabled={processPoSubmitting} />
                              {line.productImageUrl ? (
                                <img className="v9o-po-img" src={line.productImageUrl} alt={line.sku} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                              ) : (
                                <div className="v9o-po-img-empty">--</div>
                              )}
                              <div className="v9o-po-main">
                                <span className="v9o-sku">{line.sku}</span>
                                <div className={`v9o-po-hint ${line.inStockEligible ? 'in-stock' : 'not-stock'}`}>
                                  {line.inStockEligible ? `In stock: ${line.availableQty.toFixed(2)}m` : `Not in stock: ${line.availableQty.toFixed(2)}m`}
                                </div>
                              </div>
                              <span className="v9o-po-qty">{line.qty.toFixed(2)}m</span>
                            </label>
                          ))}
                        </div>
                        <div className="v9o-modal-footer">
                          <button className="v9o-action-btn ghost" onClick={() => setProcessPoModalOpen(false)} disabled={processPoSubmitting}>Cancel</button>
                          <button className="v9o-action-btn primary" onClick={submitProcessPo} disabled={processPoSubmitting || processPoSelectedSkus.length === 0}>
                            {processPoSubmitting ? 'Submitting...' : 'Create PO Batch'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </React.Fragment>
        );
      })}

        {!loading && visibleRows.length === 0 && (
          <div className="v9o-empty">No orders found for current filters.</div>
        )}
      </section>

      <div className="v9o-footer">
        <button className="v9o-action-btn" onClick={loadMore} disabled={!hasMore || loadingMore || loading}>
          {loadingMore ? 'Loading...' : (hasMore ? 'Load More' : 'No More Orders')}
        </button>
      </div>
    </div>
  );
}

export default V9OrdersPage;
